import { getLocalSellingShelves, type LocationSellingContextDto, type LocalShelfSellingDto } from "./api.ts";
// ONBW1-S3 (docs/build-briefs/ONBW1-S3-owner-wizard.md; DEC-416; docs/design/onboarding-wizard.md; mockups
// design/mockups/onboarding-wizard/). The location owner's guided setup, on the device at the location: twelve steps in six chapters, one
// question per screen, then You are ready to sell and What is left. It replaces ONB4's seven-step wizard on the same screen
// (#setup-wizard-screen) and keeps what ONB4 built under it: the card account read, the reader registration, the test sale
// and the Stripe return.
//
// STATE. Step state is computed from the 15-item checklist (admin.ts readSetupChecklistSnapshot) and the settings answer
// wherever they can say; the little they cannot (skipped, reviewed, Save and finish later, finished) is the one stored record
// per location (GET and PUT .../setup-wizard). Progress is saved on every Continue. Every write goes through a route Settings
// already calls, except the progress record and the help request. Labels, states and one blocking reason: no explanatory
// copy. All text via textContent.
//
// The step screens are in this file; the optional extras (cash, receipts, where guests tip, the printer, vendors, another
// device) are in setupWizardExtras.ts, which reaches this file only through the WizardCtx below.

import {
  createLiquorProduct,
  quoteInventorySale,
  createMenuDrink,
  createUser,
  getCardPayments,
  getAccountingStatus,
  getCardTestSale,
  getPayrollGusto,
  getRoyaltyAuthorization,
  getSettings,
  getSubscriptionDebitAuthorization,
  getLegalAcceptance,
  getSetupWizard,
  postLegalAcceptance,
  getTaxRates,
  getTaxSuggestion,
  getVocabularies,
  listLiquorProducts,
  listTenantRoles,
  listTerminals,
  listUsersForAdmin,
  postCardPaymentsOnboardingLink,
  postCardPaymentsReturn,
  postAccountingConnectionStart,
  postCardPaymentsSetUp,
  postPayrollConnect,
  postRoyaltyAuthorizationReturn,
  postRoyaltyAuthorizationStart,
  postSubscriptionDebitAuthorizationReturn,
  postSubscriptionDebitAuthorizationStart,
  putLocationAddress,
  putSetupWizard,
  putTaxRates,
  recordOpeningBalanceMovement,
  registerTerminal,
  setStartsEmpty,
  startCardTestSale,
  updateSettings,
  type AccountingStatusDto,
  type AdminUserDto,
  type CardTestSaleDto,
  type PayrollStatusDto,
  type LiquorProductDto,
  type RoyaltyAuthorizationDto,
  type SubscriptionDebitDto,
  type TenantRoleDto,
  type TenantSettingsDto,
  type TerminalDto,
  type VocabularyValueDto,
} from "./api.ts";
import {
  openOnboardingTab,
  requestOnboardingTarget,
  reviewPoller,
  TAB_BLOCKED_LINE,
  watchReturnFromStripe,
} from "./cardPayments.ts";
import {
  onboardingNavigationTarget,
  reviewStateOf,
  type CardPaymentsArrival,
  type CardPaymentsForLocationDto,
} from "./cardPaymentsModel.ts";
import { openInventoryImport, resetInventoryImport } from "./inventoryImport.ts";
import { formatCents } from "./money.ts";
import { cutoffText } from "./reportFormat.ts";
import {
  ADDRESS_NEEDS,
  BOTTLE_SIZES,
  CLOSING_CHOICES,
  CLOSING_DEFAULT,
  CLOSING_HOUR_CHOICES,
  CLOSING_HOUR_HINT,
  DEFAULT_TIP_BASIS_POINTS,
  DRINK_POUR_CHOICES,
  DRINK_STARTERS,
  FIRST_READER_NAME,
  MAX_BOTTLES,
  MAX_PRICE_CENTS,
  MAX_TAX_BASIS_POINTS,
  MAX_TIP_BASIS_POINTS,
  READER_NEEDS_WORDS,
  TAX_PARTS,
  ZONE_CHOICES,
  addressBlockedReason,
  addressIsComplete,
  allStepStates,
  bottleBlockedReason,
  chapterStates,
  dollarText,
  drinkBlockedReason,
  drinkCategoryFor,
  drinkName,
  drinkPriceView,
  EMPTY_PROGRESS,
  firstUnfinishedIndex,
  initialsOf,
  nextIndex,
  opensWelcomeBack,
  ouncesText,
  ownerSteps,
  percentText,
  placeLabel,
  previousIndex,
  readerCodeFromWords,
  readyLines,
  readyView,
  requiredStepCount,
  setTaxPart,
  stepBottleCount,
  stepLine,
  stepTaxPart,
  taxBlockedReason,
  taxComponentsToRecord,
  taxDraftFromSuggestion,
  taxSuggestionOf,
  taxSumLine,
  taxTag,
  taxTotal,
  tipText,
  tipsBlockedReason,
  useRateLabel,
  welcomeBackView,
  whatsLeftView,
  zoneLabel,
  zoneSuggestionOf,
  type AddressDraft,
  type BottleDraft,
  type DrinkDraft,
  type OwnerCardReads,
  type OwnerExtraId,
  type OwnerReads,
  type OwnerStepDef,
  type OwnerStepId,
  type OwnerStepState,
  type TaxDraft,
  type TaxPartId,
  type TaxSuggestionView,
  type WizardProgress,
  type ZoneSuggestion,
} from "./ownerWizardModel.ts";
import { hasPermission, pinLengthRangeText, type Session } from "./session.ts";
import { isFollowableConsentUrl } from "./accounting.ts";
import { DEFAULT_THEME_ID } from "./theme.ts";
import { LEGAL_PRIVACY_URL, LEGAL_TERMS_URL, LEGAL_TERMS_VERSION } from "../../src/domain/legalTerms.ts";
import {
  button,
  card,
  chipRow,
  el,
  field,
  markChosen,
  openPadSheet,
  pickCard,
  readerDrawing,
  selectInput,
  stepper,
  tag,
  textInput,
} from "./setupWizardUi.ts";
import { renderExtraScreen, resetExtras, type ExtraHost } from "./setupWizardExtras.ts";
import {
  cardPaymentsPhase,
  cardPaymentsStepView,
  pinAppend,
  pinBackspace,
  pinDotCount,
  personFormView,
  readerConnectionTag,
  clearStripeReturnMarker,
  setStripeReturnMarker,
  stateChoices,
  takeStripeReturnMarker,
  testSaleIntro,
  testSaleNotice,
  testSaleOutcome,
  TEST_SALE_POLL_MS,
  type ChecklistStates,
  type PersonDraft,
} from "./setupWizardModel.ts";
import {
  setRoyaltyReturnMarker,
  takeRoyaltyReturnMarker,
  type RoyaltyArrival,
} from "./royaltyAuthorizationModel.ts";
import {
  bankAccountsView,
  payoutSettingsUrl,
  setSubscriptionDebitReturnMarker,
  takeSubscriptionDebitReturnMarker,
  type BankRowId,
  type BankRowView,
  type SubscriptionDebitArrival,
} from "./bankAccountsModel.ts";
import { CARD_READER_REGISTERED_EVENT } from "./terminalSignInModel.ts";
import { US_STATE_NAMES } from "./softwareOwnerLegalModel.ts";
import { buildRequest, classifyItem, type Entry, type StockItem } from "./startingStockModel.ts";

/** What the host (main.ts) supplies, so this module never reaches into the floor screens itself. */
export interface SetupWizardDeps {
  getSession: () => Session | undefined;
  /** The location's name when this device knows it. */
  locationName: () => string | undefined;
  /** The 16 checklist reads, fresh (admin.ts readSetupChecklistSnapshot). Undefined when not permitted or the
   * session ended meanwhile. */
  readChecklist: (
    session: Session,
  ) => Promise<{ states: ChecklistStates; details: Partial<Record<string, string>> } | undefined>;
  /** Shows the wizard screen (hides every other). */
  showScreen: () => void;
  /** Save and finish later, and Open tabs: Open Tabs, with the first-day banner. */
  finishLater: () => void;
  /** The location this device is tied to. */
  deviceLocationId: () => string | undefined;
}

let deps: SetupWizardDeps | undefined;

export function configureSetupWizard(next: SetupWizardDeps): void {
  deps = next;
}

function requireDeps(): SetupWizardDeps {
  if (deps === undefined) throw new Error("setup wizard used before it was configured");
  return deps;
}

function find<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) throw new Error(`expected element for selector "${selector}"`);
  return element;
}

const steplineEl = find<HTMLElement>("#setup-wizard-stepline");
const segmentsEl = find<HTMLElement>("#setup-wizard-segments");
const finishLaterButton = find<HTMLButtonElement>("#setup-wizard-finish-later");
const bodyEl = find<HTMLElement>("#setup-wizard-body");
const backButton = find<HTMLButtonElement>("#setup-wizard-back");
const secondaryButton = find<HTMLButtonElement>("#setup-wizard-secondary");
const secondaryMoreButton = find<HTMLButtonElement>("#setup-wizard-secondary-more");
const reasonEl = find<HTMLElement>("#setup-wizard-reason");
const primaryButton = find<HTMLButtonElement>("#setup-wizard-primary");
const mainEl = find<HTMLElement>(".setup-wizard-main");
const wizardScreenEl = find<HTMLElement>("#setup-wizard-screen");

/** The e2e static server serves index.html with this tag set to "off" (about 280 specs sign in as a first-time Admin and
 * expect Open Tabs). A deployed index.html carries no tag, so the wizard is on. */
export function setupWizardEnabled(): boolean {
  const tagEl = document.querySelector<HTMLMetaElement>('meta[name="setup-wizard"]');
  return tagEl === null || tagEl.content !== "off";
}

// ---------------------------------------------------------------------------------------------------
// State held only while the wizard is open
// ---------------------------------------------------------------------------------------------------

type Screen =
  | { kind: "terms" }
  | { kind: "step"; index: number }
  | { kind: "back" }
  | { kind: "ready" }
  | { kind: "left" }
  | { kind: "extra"; id: OwnerExtraId };

let session: Session | undefined;
let open = false;
let busy = false;
let screen: Screen = { kind: "step", index: 0 };
/** LEGAL1-F1: the screen to show once the owner has agreed to the Terms of Service and Privacy Policy. */
let afterTerms: Screen = { kind: "step", index: 0 };
let termsTicked = false;
let checklistStates: ChecklistStates = {};
let settings: TenantSettingsDto | undefined;
let progress: WizardProgress = EMPTY_PROGRESS;
let primaryAction: (() => void) | undefined;
let secondaryAction: (() => void) | undefined;
let secondaryMoreAction: (() => void) | undefined;
let backAction: (() => void) | undefined;

// Your location and Money.
let addressDraft: AddressDraft | undefined;
let zoneSuggestion: ZoneSuggestion = { kind: "none" };
let taxSuggestion: TaxSuggestionView = {
  available: false,
  parts: { state: 0, county: 0, city: 0, special_district: 0 },
  asOf: null,
};
/** The zone the owner confirmed on screen 2 (or picked instead). */
let chosenZone: string | undefined;
let zonePicking = false;
let closingChoice: string | undefined;
let closingOther = false;
let taxDraft: TaxDraft | undefined;
/** The owner tapped Use on this screen, or the rate was already confirmed when the screen opened. */
let taxConfirmedNow = false;
let taxEditing = false;
/** The step opened on a rate someone already saved for this location (not yet confirmed here), not on the suggestion. */
let taxFromSaved = false;
let tipDraft: number[] | undefined;
let tipsChanging = false;

// Drinks.
type BottleWayChoice = "upload" | "hand" | "later";
let bottleWay: BottleWayChoice | undefined;
const setupSelling: { context?: LocationSellingContextDto; siteId?: string; shelves: Map<string,LocalShelfSellingDto> } = { shelves: new Map() };
let bottleCreateSellingRequestId = crypto.randomUUID();
let pendingBottleCreate: { session: Session; siteId: string | undefined; body: Parameters<typeof createLiquorProduct>[2]; uncertain: boolean } | undefined;
let bottleFormOpen = false;
let bottleDraft: BottleDraft = { name: "", category: "", size: "", shelfTier: "" };
let addedBottles: LiquorProductDto[] = [];
let vocab: Record<string, VocabularyValueDto[]> = {};
let bottles: LiquorProductDto[] = [];
let drinkDraft: DrinkDraft = emptyDrinkDraft();
let stockCounts = new Map<string, number>();

// People.
let users: AdminUserDto[] = [];
let roles: TenantRoleDto[] = [];
let teamFormOpen = false;
let personDraft: PersonDraft = { name: "", role: "", pin: "" };

// Card payments. Read fresh on opening and after each card action.
let cardView: CardPaymentsForLocationDto | undefined;
let cardLoaded = false;
let stripeArrived = false;
let locationReader: TerminalDto | undefined;
let testEnvironment = false;
let readerWords: [string, string, string] = ["", "", ""];
let testSale: { id: string; status: CardTestSaleDto["status"]; message: string } | undefined;
let testSaleApproved = false;
let testSaleTimer: ReturnType<typeof setTimeout> | undefined;
let royaltyView: RoyaltyAuthorizationDto | undefined;
/** ACCTEXPORT1 and PAYEXPORT1: what the QuickBooks and Gusto reads answered; undefined when the feature is off or not allowed. */
let accountingView: AccountingStatusDto | undefined;
let payrollView: PayrollStatusDto | undefined;
// BANK1: the Snip & Sip subscription's debit authorization, and the row (if any) whose authorization text is open.
let subscriptionView: SubscriptionDebitDto | undefined;
let bankConfirming: BankRowId | undefined;

function emptyDrinkDraft(): DrinkDraft {
  return { starterId: "", otherName: "", liquorProductId: "", ounces: null, buildChargeCents: 0 };
}

function live(token: string): boolean {
  return (
    open && session !== undefined && session.token === token && deps?.getSession()?.token === token
  );
}

function cardLive(): boolean {
  return cardView !== undefined;
}

function cardPhase(): ReturnType<typeof cardPaymentsPhase> {
  return cardPaymentsPhase({
    kind: cardView?.thisLocation.kind,
    arrived: stripeArrived,
    review: cardView === undefined ? undefined : reviewStateOf(cardView.thisLocation, new Date()),
  });
}

function steps(): readonly OwnerStepDef[] {
  return ownerSteps({
    cardAccountVisible: cardLive(),
    royaltyOffered: bankStepOffered(),
    connectionsOffered: connectionsStepOffered(),
  });
}

/** The optional Connections step is offered only while at least one of QuickBooks and Gusto is on for this location and person. */
function connectionsStepOffered(): boolean {
  return accountingView !== undefined || payrollView !== undefined;
}

function anyConnectionMade(): boolean {
  return accountingView?.connection.status === "connected" || payrollView?.connection.state === "connected";
}

/** BANK1. The three rows as the wizard reads them now. */
function bankView(): ReturnType<typeof bankAccountsView> {
  return bankAccountsView({
    card: cardView,
    royalty: royaltyView,
    subscription: subscriptionView,
    canOnboard: session !== undefined && hasPermission(session, "admin.payments.onboard"),
  });
}

/** BANK1. The Bank accounts step is offered when the server offers a debit authorization (Royalties or Snip & Sip
 * subscription). Deposits has its own Card payments step, so it alone never adds a step; it appears in this one for context. */
function bankStepOffered(): boolean {
  return royaltyView?.applies === true || subscriptionView?.applies === true;
}

function cardReads(): OwnerCardReads | undefined {
  if (!cardLoaded) return undefined;
  return {
    accountLive: cardLive(),
    accountEnabled: cardPhase() === "on",
    readerRegistered: locationReader !== undefined,
    testSaleApproved,
  };
}

function addressOf(value: TenantSettingsDto): AddressDraft {
  return {
    street: value.locationAddress.addressLine1 ?? "",
    city: value.locationAddress.city ?? "",
    state: value.locationAddress.state ?? "",
    postalCode: value.locationAddress.postalCode ?? "",
  };
}

function reads(): OwnerReads {
  return {
    states: checklistStates,
    progress,
    addressComplete: settings === undefined ? undefined : addressIsComplete(addressOf(settings)),
    card: cardReads(),
    // BANK1: the step the wizard still calls "royalty" internally (its id is stored in saved progress) is the Bank accounts
    // step. It is done when every offered account reads Connected.
    royalty: bankStepOffered()
      ? { offered: true, active: bankView().debitsConnected }
      : undefined,
    connections: connectionsStepOffered() ? { anyConnected: anyConnectionMade() } : undefined,
  };
}

function stepStates(): OwnerStepState[] {
  return allStepStates(steps(), reads());
}

/** Reads the checklist, the settings and the stored progress fresh. False when the session ended or the person may not read
 * them. */
async function refreshReads(active: Session): Promise<boolean> {
  const d = requireDeps();
  const siteId = d.deviceLocationId();
  const [snapshot, settingsResult, localShelves] = await Promise.all([
    d.readChecklist(active),
    getSettings(active.apiBaseUrl, active.tenantId, siteId),
    hasPermission(active, "admin.liquor_product.view")
      ? getLocalSellingShelves(active.apiBaseUrl, active.tenantId, siteId)
      : Promise.resolve(undefined),
  ]);
  if (!live(active.token) || siteId !== d.deviceLocationId()) return false;
  if (snapshot === undefined || settingsResult.status !== 200) return false;
  checklistStates = snapshot.states;
  settings = settingsResult.body.settings;
  setupSelling.context = settingsResult.body.sellingContext;
  setupSelling.siteId = siteId;
  setupSelling.shelves = new Map(
    localShelves?.status === 200 && localShelves.body.sellingContext.locationId === siteId
      ? localShelves.body.shelves.flatMap((tier) =>
          tier.selling === undefined ? [] : [[tier.value, tier.selling] as const],
        )
      : [],
  );
  return true;
}

/** The card payments read and this location's readers. A read that cannot be made (the feature is dark for this deployment, or the
 * person may not look) leaves the account step hidden and no reader, never a guess. */
async function refreshCardReads(active: Session): Promise<void> {
  const canLook = hasPermission(active, "admin.terminal.view");
  const [paymentsResult, terminalsResult, royaltyResult, subscriptionResult] = canLook
    ? await Promise.all([
        getCardPayments(active.apiBaseUrl, active.tenantId),
        listTerminals(active.apiBaseUrl, active.tenantId),
        getRoyaltyAuthorization(active.apiBaseUrl, active.tenantId),
        getSubscriptionDebitAuthorization(active.apiBaseUrl, active.tenantId),
      ])
    : [undefined, undefined, undefined, undefined];
  if (!live(active.token)) return;
  subscriptionView =
    subscriptionResult?.status === 200 && subscriptionResult.body.applies === true
      ? subscriptionResult.body
      : undefined;
  royaltyView =
    royaltyResult?.status === 200 && royaltyResult.body.applies === true
      ? royaltyResult.body
      : undefined;
  cardView =
    paymentsResult?.status === 200 && Array.isArray(paymentsResult.body.otherLocations)
      ? paymentsResult.body
      : undefined;
  if (terminalsResult?.status === 200) {
    const locationId = requireDeps().deviceLocationId();
    locationReader = terminalsResult.body.terminals.find(
      (terminal) =>
        terminal.active && (locationId === undefined || terminal.location_id === locationId),
    );
    testEnvironment = terminalsResult.body.testEnvironment === true;
  } else {
    locationReader = undefined;
    testEnvironment = false;
  }
  cardLoaded = true;
  await refreshConnectionReads(active);
}

/** The QuickBooks and Gusto reads behind the optional Connections step. A read that is refused (feature off, not allowed) leaves the
 * step out. */
async function refreshConnectionReads(active: Session): Promise<void> {
  const siteId = requireDeps().deviceLocationId();
  const [accounting, payroll] = await Promise.all([
    siteId !== undefined && hasPermission(active, "admin.tenant_settings.view")
      ? getAccountingStatus(active.apiBaseUrl, active.tenantId, siteId)
      : Promise.resolve(undefined),
    getPayrollGusto(active.apiBaseUrl, active.tenantId),
  ]);
  if (!live(active.token)) return;
  accountingView = accounting?.status === 200 && accounting.body.enabled === true ? accounting.body : undefined;
  payrollView = payroll.status === 200 && typeof payroll.body.canManage === "boolean" ? payroll.body : undefined;
}

/** The location's address and the suggestions that come from it, read once and again whenever the address is saved. */
async function refreshSuggestion(active: Session): Promise<void> {
  const locationId = requireDeps().deviceLocationId();
  if (locationId === undefined || !hasPermission(active, "admin.tax_rate.manage")) {
    zoneSuggestion = { kind: "none" };
    taxSuggestion = taxSuggestionOf(undefined);
    return;
  }
  const result = await getTaxSuggestion(active.apiBaseUrl, active.tenantId, locationId);
  if (!live(active.token)) return;
  if (result.status === 200) {
    zoneSuggestion = zoneSuggestionOf(result.body.timeZone);
    taxSuggestion = taxSuggestionOf(result.body.tax);
  } else {
    zoneSuggestion = { kind: "none" };
    taxSuggestion = taxSuggestionOf(undefined);
  }
}

function resetDrafts(): void {
  addressDraft = undefined;
  zoneSuggestion = { kind: "none" };
  taxSuggestion = taxSuggestionOf(undefined);
  chosenZone = undefined;
  zonePicking = false;
  closingChoice = undefined;
  closingOther = false;
  taxDraft = undefined;
  taxConfirmedNow = false;
  taxEditing = false;
  tipDraft = undefined;
  tipsChanging = false;
  bottleWay = undefined;
  pendingBottleCreate = undefined;
  bottleFormOpen = false;
  bottleDraft = { name: "", category: "", size: "", shelfTier: "" };
  addedBottles = [];
  vocab = {};
  bottles = [];
  drinkDraft = emptyDrinkDraft();
  stockCounts = new Map();
  users = [];
  roles = [];
  teamFormOpen = false;
  personDraft = { name: "", role: "", pin: "" };
  cardView = undefined;
  cardLoaded = false;
  stripeArrived = false;
  locationReader = undefined;
  testEnvironment = false;
  readerWords = ["", "", ""];
  testSale = undefined;
  testSaleApproved = false;
  royaltyView = undefined;
  subscriptionView = undefined;
  accountingView = undefined;
  payrollView = undefined;
  bankConfirming = undefined;
}

// ---------------------------------------------------------------------------------------------------
// Opening
// ---------------------------------------------------------------------------------------------------

export interface OpenOptions {
  atStep?: OwnerStepId;
  arrival?: CardPaymentsArrival;
  royaltyArrival?: RoyaltyArrival;
  subscriptionArrival?: SubscriptionDebitArrival;
  /** True: show Welcome back only when the owner chose Save and finish later (a sign-in). */
  onlyIfPaused?: boolean;
}

/** Opens the wizard at the first unfinished step, or at Welcome back when the owner chose Save and finish later. False (nothing
 * shown) when the person may not see Settings location setup, when every checklist item is done, or when the reads could not be
 * made. */
export async function openSetupWizard(
  active: Session,
  options: OpenOptions = {},
): Promise<boolean> {
  const d = requireDeps();
  if (!hasPermission(active, "admin.tenant_settings.view")) return false;
  const [snapshot, settingsResult, progressResult, legalResult] = await Promise.all([
    d.readChecklist(active),
    getSettings(active.apiBaseUrl, active.tenantId),
    getSetupWizard(active.apiBaseUrl, active.tenantId),
    getLegalAcceptance(active.apiBaseUrl, active.tenantId),
  ]);
  if (d.getSession()?.token !== active.token) return false;
  if (snapshot === undefined || settingsResult.status !== 200) return false;
  const stored: WizardProgress =
    progressResult.status === 200
      ? {
          skippedSteps: progressResult.body.progress.skippedSteps,
          reviewedSteps: progressResult.body.progress.reviewedSteps,
          pausedAt: progressResult.body.progress.pausedAt,
          finishedAt: progressResult.body.progress.finishedAt,
        }
      : EMPTY_PROGRESS;
  if (options.onlyIfPaused === true && !opensWelcomeBack(stored)) return false;
  // Back from Stripe opens it even with all 16 done: the card steps are still the point.
  if (
    options.arrival === undefined &&
    options.royaltyArrival === undefined &&
    options.subscriptionArrival === undefined &&
    CHECKLIST_COUNT(snapshot.states) >= OWNER_CHECKLIST_TOTAL
  ) {
    return false;
  }
  stopTestSalePolling();
  session = active;
  checklistStates = snapshot.states;
  settings = settingsResult.body.settings;
  progress = stored;
  resetDrafts();
  resetInventoryImport();
  busy = false;
  open = true;
  await Promise.all([refreshCardReads(active), refreshSuggestion(active)]);
  if (!live(active.token)) return false;
  const list = steps();
  const states = stepStates();
  let next: Screen;
  if (options.atStep !== undefined && list.some((step) => step.id === options.atStep)) {
    next = { kind: "step", index: list.findIndex((step) => step.id === options.atStep) };
  } else if (opensWelcomeBack(progress)) {
    next = { kind: "back" };
  } else {
    const at = firstUnfinishedIndex(states);
    next =
      at === undefined
        ? progress.finishedAt === null
          ? { kind: "ready" }
          : { kind: "left" }
        : { kind: "step", index: at };
  }
  // LEGAL1-F1: until the location owner has agreed to the Terms of Service and Privacy Policy in force, the first screen is
  // the agreement. Coming back from Stripe skips it (the owner already passed it to get there).
  const needsTerms =
    options.arrival === undefined &&
    options.royaltyArrival === undefined &&
    options.subscriptionArrival === undefined &&
    canSaveProgress(active) &&
    legalResult.status === 200 &&
    !legalResult.body.acceptance.current;
  afterTerms = next;
  termsTicked = false;
  screen = needsTerms ? { kind: "terms" } : next;
  d.showScreen();
  render();
  if (options.arrival !== undefined) await handleStripeArrival(options.arrival);
  if (options.royaltyArrival !== undefined) await handleBankArrival("royalties", options.royaltyArrival);
  if (options.subscriptionArrival !== undefined)
    await handleBankArrival("subscription", options.subscriptionArrival);
  return true;
}

const OWNER_CHECKLIST_TOTAL = 15;
function CHECKLIST_COUNT(states: ChecklistStates): number {
  return Object.values(states).filter((state) => state === "done").length;
}

/** A sign-in: Welcome back opens only when the owner chose Save and finish later. False (nothing shown) otherwise. */
export function openSetupWizardIfPaused(active: Session): Promise<boolean> {
  return openSetupWizard(active, { onlyIfPaused: true });
}

/** Back from Stripe's hosted bank debit page (`?royalty_authorization=return|cancel`), when the wizard is what sent the
 * browser there. False (nothing shown, marker gone) when it was not, so the caller opens Settings. */
export async function openSetupWizardOnRoyaltyReturn(
  active: Session,
  arrival: RoyaltyArrival,
): Promise<boolean> {
  if (!setupWizardEnabled() || !takeRoyaltyReturnMarker(window.sessionStorage)) return false;
  return openSetupWizard(active, { atStep: "royalty", royaltyArrival: arrival });
}

/** BANK1. Back from Stripe's hosted bank debit page for the Snip & Sip subscription (`?subscription_debit=return|cancel`), when
 * the wizard is what sent the browser there. False (nothing shown, marker gone) when it was not, so the caller opens Settings. */
export async function openSetupWizardOnSubscriptionDebitReturn(
  active: Session,
  arrival: SubscriptionDebitArrival,
): Promise<boolean> {
  if (!setupWizardEnabled() || !takeSubscriptionDebitReturnMarker(window.sessionStorage)) return false;
  return openSetupWizard(active, { atStep: "royalty", subscriptionArrival: arrival });
}

/** Back from Stripe's page (`?card_payments=return|refresh`), when the wizard is what sent the browser there. False (nothing
 * shown, marker gone) when it was not, so the caller opens Settings as before. */
export async function openSetupWizardOnStripeReturn(
  active: Session,
  arrival: CardPaymentsArrival,
): Promise<boolean> {
  if (!setupWizardEnabled() || !takeStripeReturnMarker(window.sessionStorage)) return false;
  return openSetupWizard(active, { atStep: "cardAccount", arrival });
}

/** Sign-out, or leaving for Settings: nothing the wizard drew outlives the session. */
export function resetSetupWizard(): void {
  stopTestSalePolling();
  resetExtras();
  open = false;
  busy = false;
  session = undefined;
  settings = undefined;
  progress = EMPTY_PROGRESS;
  checklistStates = {};
  resetDrafts();
  primaryAction = undefined;
  secondaryAction = undefined;
  secondaryMoreAction = undefined;
  backAction = undefined;
  bodyEl.replaceChildren();
  segmentsEl.replaceChildren();
  reasonEl.textContent = "";
  steplineEl.textContent = "";
}

// ---------------------------------------------------------------------------------------------------
// Saving progress
// ---------------------------------------------------------------------------------------------------

function canSaveProgress(active: Session): boolean {
  return hasPermission(active, "admin.location_settings.manage");
}

function without(list: readonly string[], drop: readonly string[]): string[] {
  return list.filter((entry) => !drop.includes(entry));
}

function withAll(list: readonly string[], add: readonly string[]): string[] {
  return [...new Set([...list, ...add])];
}

/** Saves what a Continue, a Skip or a pause changes, to the location's stored record. A person who may not write it keeps it for the
 * session only. False (and a reason shown) when the save failed, so the screen does not move on over a lost save. */
async function commit(change: {
  review?: readonly string[];
  skip?: readonly string[];
  unskip?: readonly string[];
  paused?: boolean;
  finished?: boolean;
}): Promise<boolean> {
  const active = session;
  if (active === undefined) return false;
  const skippedSteps = withAll(
    without(progress.skippedSteps, change.unskip ?? []),
    change.skip ?? [],
  );
  const reviewedSteps = withAll(progress.reviewedSteps, change.review ?? []);
  if (!canSaveProgress(active)) {
    progress = {
      skippedSteps,
      reviewedSteps,
      pausedAt:
        change.paused === undefined
          ? progress.pausedAt
          : change.paused
            ? new Date().toISOString()
            : null,
      finishedAt:
        change.finished === true
          ? (progress.finishedAt ?? new Date().toISOString())
          : progress.finishedAt,
    };
    return true;
  }
  const result = await putSetupWizard(active.apiBaseUrl, active.tenantId, {
    skippedSteps,
    reviewedSteps,
    ...(change.paused === undefined ? {} : { paused: change.paused }),
    ...(change.finished === undefined ? {} : { finished: change.finished }),
  });
  if (!live(active.token)) return false;
  if (result.status !== 200) {
    showReason(result.body.message ?? result.body.error ?? "Could not save. Try again.");
    return false;
  }
  progress = {
    skippedSteps: result.body.progress.skippedSteps,
    reviewedSteps: result.body.progress.reviewedSteps,
    pausedAt: result.body.progress.pausedAt,
    finishedAt: result.body.progress.finishedAt,
  };
  return true;
}

// ---------------------------------------------------------------------------------------------------
// Frame
// ---------------------------------------------------------------------------------------------------

function render(): void {
  if (!open || session === undefined) return;
  const list = steps();
  const states = stepStates();
  if (screen.kind !== "step" || list[screen.index]?.id !== "testSale") stopTestSalePolling();

  bodyEl.replaceChildren();
  reasonEl.textContent = "";
  backButton.hidden = true;
  backAction = undefined;
  secondaryButton.hidden = true;
  secondaryButton.disabled = false;
  secondaryAction = undefined;
  secondaryMoreButton.hidden = true;
  secondaryMoreButton.disabled = false;
  secondaryMoreAction = undefined;
  primaryButton.disabled = false;
  primaryButton.classList.remove("ow-btn-good");
  primaryAction = undefined;
  // The wizard is drawn in the light brass look while the default theme is showing; a theme the person chose stands.
  if (document.documentElement.dataset["theme"] === DEFAULT_THEME_ID)
    wizardScreenEl.dataset["theme"] = "brass";
  else delete wizardScreenEl.dataset["theme"];
  mainEl.dataset["screen"] = screen.kind === "step" ? (list[screen.index]?.id ?? "") : screen.kind;

  const chapterless = screen.kind === "back" || screen.kind === "left" || screen.kind === "terms";
  finishLaterButton.hidden = chapterless;
  segmentsEl.hidden = chapterless;
  segmentsEl.replaceChildren();
  if (!chapterless) {
    const current =
      screen.kind === "step"
        ? { kind: "step" as const, index: screen.index }
        : { kind: "ready" as const };
    for (const chapter of chapterStates(list, states, current)) {
      segmentsEl.append(
        el(
          "li",
          {
            cls: "setup-wizard-segment",
            testid: "setup-wizard-segment",
            attrs: { "data-chapter": chapter.id, "data-state": chapter.state },
          },
          el("i", { attrs: { "aria-hidden": "true" } }),
          el("b", { text: chapter.state === "done" ? `✓ ${chapter.label}` : chapter.label }),
        ),
      );
    }
  }

  switch (screen.kind) {
    case "terms":
      steplineEl.textContent = "";
      renderTerms();
      break;
    case "step": {
      const step = list[screen.index];
      if (step === undefined) return;
      steplineEl.textContent = stepLine(screen.index, requiredStepCount(list), step.optional === true);
      const previous = previousIndex(screen.index);
      if (previous !== undefined) {
        backButton.hidden = false;
        backAction = () => {
          screen = { kind: "step", index: previous };
          render();
        };
      }
      renderStep(step, screen.index);
      break;
    }
    case "back":
      steplineEl.textContent = "";
      renderWelcomeBack();
      break;
    case "ready":
      steplineEl.textContent = readyView(
        list,
        states,
        testSaleApproved || progress.reviewedSteps.includes("testSale"),
      ).ready
        ? `All ${list.length} steps done`
        : "";
      renderReady();
      break;
    case "left":
      steplineEl.textContent = "";
      renderWhatsLeft();
      break;
    case "extra":
      steplineEl.textContent = "Extra";
      renderExtra(screen.id);
      break;
  }
  bodyEl.scrollTop = 0;
}

function column(wide = false): HTMLElement {
  const col = el("div", { cls: wide ? "ow-col ow-col-wide" : "ow-col" });
  bodyEl.append(col);
  return col;
}

function heading(col: HTMLElement, kicker: string, title: string, hint?: string): void {
  col.append(
    el("div", { cls: "ow-kicker", text: kicker, testid: "setup-wizard-eyebrow" }),
    el("h1", {
      cls: "ow-title",
      text: title,
      testid: "setup-wizard-title",
      attrs: { tabindex: "-1" },
    }),
  );
  if (hint !== undefined)
    col.append(el("p", { cls: "ow-hint", text: hint, testid: "setup-wizard-hint" }));
}

function setPrimary(label: string, enabled: boolean, reason: string, action: () => void): void {
  primaryButton.textContent = label;
  primaryButton.disabled = !enabled;
  reasonEl.textContent = reason;
  primaryAction = enabled ? action : undefined;
}

function setSecondary(label: string, action: () => void): void {
  secondaryButton.textContent = label;
  secondaryButton.hidden = false;
  secondaryAction = action;
}

function setSecondaryMore(label: string, action: () => void): void {
  secondaryMoreButton.textContent = label;
  secondaryMoreButton.hidden = false;
  secondaryMoreAction = action;
}

function setBack(action: () => void): void {
  backButton.hidden = false;
  backAction = action;
}

function showReason(text: string): void {
  reasonEl.textContent = text;
}

/** Runs one action with the buttons held, so a double tap cannot save twice. */
async function runBusy(action: () => Promise<void>): Promise<void> {
  if (busy) return;
  busy = true;
  primaryButton.disabled = true;
  try {
    await action();
  } finally {
    busy = false;
    if (open && primaryAction !== undefined) primaryButton.disabled = false;
  }
}

function goToStep(id: OwnerStepId): void {
  const at = steps().findIndex((step) => step.id === id);
  if (at < 0) return;
  screen = { kind: "step", index: at };
  render();
}

/** Continue: the next step that is not done, or the ready screen after the last. */
async function goNext(): Promise<void> {
  if (screen.kind !== "step") return;
  const target = nextIndex(screen.index, stepStates());
  if (target === undefined) {
    await showReady();
    return;
  }
  screen = { kind: "step", index: target };
  render();
}

/** Continue on a step: saves the review (and un-skips the step), refreshes the reads, then moves on. */
async function continueStep(
  review: readonly string[] = [],
  also: { refresh?: boolean } = {},
): Promise<void> {
  const active = session;
  if (active === undefined || screen.kind !== "step") return;
  const id = steps()[screen.index]?.id;
  if (id === undefined) return;
  await runBusy(async () => {
    if (!(await commit({ review, unskip: [id] }))) return;
    if (also.refresh !== false) await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

/** Skip for now: remembered on the server, listed on What is left, and on to the next step. */
async function skipStep(): Promise<void> {
  const active = session;
  if (active === undefined || screen.kind !== "step") return;
  const id = steps()[screen.index]?.id;
  if (id === undefined) return;
  await runBusy(async () => {
    if (!(await commit({ skip: [id] }))) return;
    if (!live(active.token)) return;
    await goNext();
  });
}

async function showReady(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await refreshReads(active);
  if (!live(active.token)) return;
  const list = steps();
  const view = readyView(
    list,
    stepStates(),
    testSaleApproved || progress.reviewedSteps.includes("testSale"),
  );
  if (view.ready && progress.finishedAt === null) {
    // The first time the owner reaches You are ready to sell it is stamped. A failed stamp is shown and the screen stays.
    if (!(await commit({ finished: true, paused: false }))) return;
  }
  screen = { kind: "ready" };
  render();
}

function showLeft(): void {
  screen = { kind: "left" };
  render();
}

async function saveAndFinishLater(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    if (!(await commit({ paused: true }))) return;
    if (!live(active.token)) return;
    const d = requireDeps();
    resetSetupWizard();
    d.finishLater();
  });
}

async function leaveToTabs(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    if (!(await commit({ paused: false }))) return;
    if (!live(active.token)) return;
    const d = requireDeps();
    resetSetupWizard();
    d.finishLater();
  });
}

// ---------------------------------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------------------------------

function renderStep(step: OwnerStepDef, index: number): void {
  switch (step.id) {
    case "address":
      renderAddress(step);
      break;
    case "timeZone":
      renderTimeZone(step);
      break;
    case "closing":
      renderClosing(step);
      break;
    case "tax":
      renderTax(step);
      break;
    case "tips":
      renderTips(step);
      break;
    case "bottles":
      renderBottles(step);
      break;
    case "firstDrink":
      renderFirstDrink(step);
      break;
    case "stock":
      renderStock(step);
      break;
    case "team":
      renderTeam(step);
      break;
    case "cardAccount":
      renderCardAccount(step);
      break;
    case "reader":
      renderReader(step);
      break;
    case "testSale":
      renderTestSale(step);
      break;
    case "royalty":
      renderBankAccounts(step);
      break;
    case "connections":
      renderConnections(step);
      break;
  }
  void index;
}

const CHAPTER_KICKER: Record<string, string> = {
  bar: "Your location",
  money: "Money",
  drinks: "Drinks",
  people: "People",
  card: "Card payments",
  connections: "Connections",
};

function kickerFor(step: OwnerStepDef): string {
  return CHAPTER_KICKER[step.chapter] ?? "";
}

function canManageSettings(active: Session): boolean {
  return hasPermission(active, "admin.location_settings.manage");
}

// ---- 1. Where is your location -------------------------------------------------------------------------

function renderAddress(step: OwnerStepDef): void {
  const active = session;
  const current = settings;
  if (active === undefined || current === undefined) return;
  if (addressDraft === undefined) addressDraft = addressOf(current);
  const draft = addressDraft;
  const col = column();
  heading(col, kickerFor(step), step.title, "Your card reader needs this.");

  const street = textInput({
    id: "ow-street",
    testid: "ow-street",
    value: draft.street,
    autocomplete: "street-address",
  });
  const city = textInput({
    id: "ow-city",
    testid: "ow-city",
    value: draft.city,
    autocomplete: "address-level2",
  });
  const state = selectInput("ow-state", "ow-state", stateChoices(), draft.state, "Choose");
  const zip = textInput({
    id: "ow-zip",
    testid: "ow-zip",
    value: draft.postalCode,
    inputmode: "numeric",
    autocomplete: "postal-code",
    maxlength: 10,
  });
  col.append(
    el(
      "div",
      { cls: "ow-stack" },
      field("Street address", street),
      el(
        "div",
        { cls: "ow-row" },
        field("City", city, "ow-grow2"),
        field("State", state),
        field("ZIP", zip),
      ),
    ),
  );
  const writable = canManageSettings(active);
  const refresh = (): void => {
    const reason = writable ? addressBlockedReason(draft) : "Not allowed";
    const ok = writable && addressIsComplete(draft);
    setPrimary("Continue", ok, ok ? "" : reason, () => void saveAddress());
  };
  street.addEventListener("input", () => {
    draft.street = street.value;
    refresh();
  });
  city.addEventListener("input", () => {
    draft.city = city.value;
    refresh();
  });
  zip.addEventListener("input", () => {
    draft.postalCode = zip.value;
    refresh();
  });
  state.addEventListener("change", () => {
    draft.state = state.value;
    refresh();
  });
  refresh();
  void ADDRESS_NEEDS;
}

async function saveAddress(): Promise<void> {
  const active = session;
  const draft = addressDraft;
  if (active === undefined || draft === undefined) return;
  await runBusy(async () => {
    const locationId = requireDeps().deviceLocationId();
    const body = {
      addressLine1: draft.street.trim(),
      city: draft.city.trim(),
      state: draft.state,
      postalCode: draft.postalCode.trim(),
    };
    const result =
      locationId === undefined
        ? await updateSettings(active.apiBaseUrl, active.tenantId, body)
        : await putLocationAddress(active.apiBaseUrl, active.tenantId, locationId, body);
    if (!live(active.token)) return;
    if (result.status !== 200) {
      showReason(result.body.message ?? result.body.error ?? "Could not save the address");
      return;
    }
    // A new address means a new suggestion: the time zone and the tax rate come from it.
    chosenZone = undefined;
    zonePicking = false;
    taxDraft = undefined;
    taxConfirmedNow = false;
    taxEditing = false;
    await Promise.all([refreshReads(active), refreshSuggestion(active)]);
    if (!live(active.token)) return;
    addressDraft = undefined;
    if (!(await commit({ unskip: ["address"] }))) return;
    await goNext();
  });
}

// ---- 2. Is this your time zone --------------------------------------------------------------------

function stateNameOf(code: string): string {
  return (US_STATE_NAMES as Record<string, string>)[code] ?? code;
}

function renderTimeZone(step: OwnerStepDef): void {
  const active = session;
  const current = settings;
  if (active === undefined || current === undefined) return;
  const confirmedBefore =
    checklistStates["businessDay"] === "done" || progress.reviewedSteps.includes("timeZone");
  if (chosenZone === undefined && confirmedBefore) chosenZone = current.businessDayTimezone;
  // The step starts from the zone saved for this location; the address suggestion is offered only when none is saved. The
  // database column default (UTC) is a row nobody chose, so it does not count as saved. The owner still taps Yes.
  const savedZone = current.businessDayTimezone !== "UTC" ? current.businessDayTimezone : undefined;
  const suggested =
    savedZone ?? (zoneSuggestion.kind === "suggested" ? zoneSuggestion.timeZone : undefined);
  const col = column();
  heading(
    col,
    kickerFor(step),
    step.title,
    suggested !== undefined && !zonePicking
      ? savedZone !== undefined
        ? "This is the one saved for your location."
        : "We picked it from your address."
      : "Pick the one your location is in.",
  );
  const writable = canManageSettings(active);
  const showPick =
    zonePicking ||
    (suggested === undefined && chosenZone === undefined) ||
    (suggested === undefined && !confirmedBefore);
  const place = placeLabel(
    current.locationAddress.city ?? "",
    stateNameOf(current.locationAddress.state ?? ""),
  );
  const shown = chosenZone ?? suggested;
  if (!showPick && shown !== undefined) {
    col.append(
      card(
        "ow-zone-card",
        el("div", { cls: "ow-big", text: zoneLabel(shown), testid: "ow-zone-name" }),
        el("div", { cls: "ow-sub", text: place, testid: "ow-zone-place" }),
      ),
    );
    const yes = button(
      "Yes, that is right",
      "ow-zone-yes",
      chosenZone === shown
        ? "ow-btn ow-btn-primary ow-btn-wide ow-btn-on"
        : "ow-btn ow-btn-primary ow-btn-wide",
      () => {
        chosenZone = shown;
        render();
      },
    );
    yes.setAttribute("aria-pressed", chosenZone === shown ? "true" : "false");
    const other = button("No, choose another", "ow-zone-other", "ow-btn ow-btn-wide", () => {
      zonePicking = true;
      render();
    });
    col.append(el("div", { cls: "ow-row" }, yes, other));
  } else {
    const pick = chipRow(
      "ow-zone-chips",
      "ow-zone-chip",
      ZONE_CHOICES.map((choice) => ({ value: choice.timeZone, label: zoneLabel(choice.timeZone) })),
      chosenZone ?? "",
      (value) => {
        chosenZone = value;
        render();
      },
      "Time zone",
    );
    col.append(el("div", { cls: "ow-stack" }, pick));
  }
  const ready = chosenZone !== undefined;
  setPrimary(
    "Continue",
    ready && writable,
    writable ? (ready ? "" : "Tap Yes to continue") : "Not allowed",
    () => void saveTimeZone(),
  );
}

async function saveTimeZone(): Promise<void> {
  const active = session;
  const zone = chosenZone;
  if (active === undefined || zone === undefined) return;
  await runBusy(async () => {
    const stored = settings?.businessDayTimezone;
    if (stored !== zone) {
      const result = await updateSettings(active.apiBaseUrl, active.tenantId, {
        businessDayTimezone: zone,
      });
      if (!live(active.token)) return;
      if (result.status !== 200) {
        showReason(result.body.message ?? result.body.error ?? "Could not save the time zone");
        return;
      }
      settings = result.body.settings;
    }
    if (!(await commit({ review: ["timeZone"], unskip: ["timeZone"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

// ---- 3. When does your location day end ----------------------------------------------------------------

function renderClosing(step: OwnerStepDef): void {
  const active = session;
  const current = settings;
  if (active === undefined || current === undefined) return;
  const confirmed = checklistStates["businessDay"] === "done";
  if (closingChoice === undefined) {
    // DAYEND1: start from the day end the location already saved (the Admin console may have saved
    // one); CLOSING_DEFAULT only when none is saved.
    closingChoice =
      current.businessDayCutoffTimeLocal !== "" ? current.businessDayCutoffTimeLocal : CLOSING_DEFAULT;
    closingOther = !CLOSING_CHOICES.some((choice) => choice.value === closingChoice);
  }
  const col = column();
  heading(col, kickerFor(step), step.title, "Sales after this time count for the next day.");
  const chips = chipRow(
    "ow-closing-chips",
    "ow-closing-chip",
    CLOSING_CHOICES,
    closingOther ? "" : (closingChoice ?? ""),
    (value) => {
      closingChoice = value;
      closingOther = false;
      render();
    },
    "Closing time",
  );
  col.append(chips);
  const hours: Array<{ value: string; label: string }> = [...CLOSING_HOUR_CHOICES];
  if (closingChoice !== undefined && closingChoice !== "" && !hours.some((h) => h.value === closingChoice)) {
    hours.push({ value: closingChoice, label: cutoffText(closingChoice) });
  }
  if (closingOther) {
    const pick = selectInput(
      "ow-closing-other",
      "ow-closing-other",
      hours,
      closingChoice ?? "",
      "Choose",
    );
    pick.addEventListener("change", () => {
      closingChoice = pick.value;
      render();
    });
    col.append(
      el("div", { cls: "ow-stack" }, field("Time", pick)),
      el("p", { cls: "ow-hint", text: CLOSING_HOUR_HINT, testid: "ow-closing-hint" }),
    );
  } else {
    col.append(
      el(
        "div",
        { cls: "ow-stack" },
        button("Pick another time", "ow-closing-more", "ow-btn", () => {
          closingOther = true;
          render();
        }),
      ),
    );
  }
  const zoneOk = confirmed || progress.reviewedSteps.includes("timeZone");
  const writable = canManageSettings(active);
  const ok = zoneOk && writable && closingChoice !== undefined && closingChoice !== "";
  setPrimary(
    "Continue",
    ok,
    !writable
      ? "Not allowed"
      : !zoneOk
        ? "Confirm your time zone first"
        : "Most locations pick 3 AM",
    () => void saveClosing(),
  );
}

async function saveClosing(): Promise<void> {
  const active = session;
  const current = settings;
  const choice = closingChoice;
  if (active === undefined || current === undefined || choice === undefined) return;
  await runBusy(async () => {
    const result = await updateSettings(active.apiBaseUrl, active.tenantId, {
      businessDayTimezone: current.businessDayTimezone,
      businessDayCutoffTimeLocal: choice,
      confirmBusinessDay: true,
    });
    if (!live(active.token)) return;
    if (result.status !== 200) {
      showReason(result.body.message ?? result.body.error ?? "Could not save the closing time");
      return;
    }
    settings = result.body.settings;
    closingChoice = undefined;
    if (!(await commit({ unskip: ["closing"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

// ---- 4. What sales tax do you charge --------------------------------------------------------------

function todayIn(zone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    return new Date().toISOString().slice(0, 10);
  }
}

/** "Jan 20, 2026" from "2026-01-20". */
function asOfText(asOf: string | null): string {
  if (asOf === null || !/^\d{4}-\d{2}-\d{2}$/.test(asOf)) return "";
  const [year, month, day] = asOf.split("-").map(Number) as [number, number, number];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[month - 1] ?? ""} ${day}, ${year}`;
}

function renderTax(step: OwnerStepDef): void {
  const active = session;
  const current = settings;
  if (active === undefined || current === undefined) return;
  const confirmedStored = checklistStates["taxRate"] === "done";
  if (taxDraft === undefined) {
    taxDraft = taxDraftFromSuggestion(taxSuggestion);
    taxConfirmedNow = confirmedStored;
    taxEditing = false;
    taxFromSaved = false;
    // The step always starts from the rate saved for this location; the suggestion only when none is saved.
    void loadStoredRate(active);
    if (confirmedStored) {
      // Already confirmed: the screen opens on the rate in force, as one rate, until the owner changes it.
      taxDraft = {
        mode: "single",
        parts: { state: null, county: null, city: null, special_district: null },
        single: current.taxRateBasisPoints,
        edited: false,
      };
    }
  }
  const draft = taxDraft;
  const col = column(true);
  const place = placeLabel(current.locationAddress.city ?? "", current.locationAddress.state ?? "");
  heading(
    col,
    kickerFor(step),
    step.title,
    taxSuggestion.available && draft.mode === "parts" && !taxConfirmedNow && !taxFromSaved && place !== ""
      ? `Suggested for ${place}. Check each part.`
      : undefined,
  );
  const writable = hasPermission(active, "admin.tax_rate.manage");

  const modes = el(
    "div",
    {
      cls: "ow-mode",
      testid: "ow-tax-modes",
      attrs: { role: "group", "aria-label": "How to enter the rate" },
    },
    modeButton("ow-tax-mode-parts", "Break it down", draft.mode === "parts", () => {
      if (draft.mode === "parts") return;
      draft.mode = "parts";
      taxConfirmedNow = false;
      if (draft.parts.state === null)
        taxDraft = { ...taxDraftFromSuggestion(taxSuggestion), mode: "parts" };
      render();
    }),
    modeButton("ow-tax-mode-single", "Use a single combined rate", draft.mode === "single", () => {
      if (draft.mode === "single") return;
      draft.mode = "single";
      taxConfirmedNow = false;
      if (draft.single === null && taxSuggestion.available)
        draft.single = taxTotal({ ...draft, mode: "parts" });
      render();
    }),
  );
  col.append(modes);

  const total = taxTotal(draft);
  const readOnly = taxConfirmedNow && !taxEditing;
  const cols = el("div", { cls: "ow-cols" });
  const left = el("div", { cls: "ow-tax-left" });
  if (draft.mode === "parts") {
    const rows = el("div", { cls: "ow-trows", testid: "ow-tax-parts" });
    for (const part of TAX_PARTS) {
      rows.append(taxPartRow(part.id, part.label, part.optional, draft, readOnly || !writable));
    }
    left.append(rows);
  } else {
    left.append(
      card(
        "ow-tax-single",
        el("div", { cls: "ow-label", text: "Combined sales tax rate" }),
        el("button", {
          cls: "ow-bigvalue",
          text: draft.single === null ? "Tap to enter" : percentText(draft.single),
          testid: "ow-tax-single-value",
          attrs: { type: "button" },
        }),
      ),
    );
    const valueButton = left.querySelector<HTMLButtonElement>(
      "[data-testid='ow-tax-single-value']",
    );
    valueButton?.addEventListener("click", () => {
      if (readOnly || !writable) return;
      void openPadSheet({
        title: "Combined sales tax rate",
        initial: draft.single ?? 0,
        max: MAX_TAX_BASIS_POINTS,
        format: percentText,
      }).then((picked) => {
        if (picked === undefined || !live(active.token)) return;
        draft.single = picked;
        draft.edited = true;
        taxConfirmedNow = false;
        render();
      });
    });
  }
  const totalCard = el(
    "div",
    { cls: "ow-card ow-total", testid: "ow-tax-total-card" },
    el("div", { cls: "ow-label ow-caps", text: "Total sales tax" }),
    el("div", { cls: "ow-hugevalue", text: percentText(total), testid: "ow-tax-total" }),
  );
  const sum = taxSumLine(draft);
  if (sum !== "")
    totalCard.append(el("div", { cls: "ow-sumline", text: sum, testid: "ow-tax-sum" }));
  totalCard.append(
    tag(
      taxTag({
        suggestionAvailable: taxSuggestion.available,
        edited: draft.edited,
        confirmed: taxConfirmedNow,
        saved: taxFromSaved,
      }),
      taxConfirmedNow ? "ok" : "warn",
      "ow-tax-tag",
    ),
  );
  const asOf = asOfText(taxSuggestion.asOf);
  if (taxSuggestion.available && !draft.edited && !taxConfirmedNow && !taxFromSaved && asOf !== "") {
    totalCard.append(el("div", { cls: "ow-sub", text: `As of ${asOf}`, testid: "ow-tax-asof" }));
  }
  const right = el("div", { cls: "ow-tax-right" }, totalCard);
  if (taxConfirmedNow && !taxEditing) {
    right.append(
      button("Change", "ow-tax-change", "ow-btn ow-btn-wide", () => {
        taxEditing = true;
        taxConfirmedNow = false;
        if (draft.mode === "single" && taxSuggestion.available) {
          taxDraft = taxDraftFromSuggestion(taxSuggestion);
        }
        render();
      }),
    );
  } else {
    const useButton = button(
      useRateLabel(draft),
      "ow-tax-use",
      "ow-btn ow-btn-primary ow-btn-wide",
      () => void useTaxRate(),
    );
    useButton.disabled = !writable || (draft.mode === "single" && draft.single === null);
    right.append(useButton);
  }
  cols.append(left, right);
  col.append(cols);
  const reason = writable ? taxBlockedReason(draft, taxConfirmedNow) : "Not allowed";
  setPrimary("Continue", writable && taxConfirmedNow, reason, () => void continueStep(["tax"]));
}

function modeButton(
  testid: string,
  label: string,
  on: boolean,
  onTap: () => void,
): HTMLButtonElement {
  const node = el("button", {
    cls: on ? "ow-mode-btn ow-mode-on" : "ow-mode-btn",
    text: label,
    testid,
    attrs: { type: "button", "aria-pressed": on ? "true" : "false" },
  });
  node.addEventListener("click", onTap);
  return node;
}

function taxPartRow(
  part: TaxPartId,
  label: string,
  optional: boolean,
  draft: TaxDraft,
  locked: boolean,
): HTMLElement {
  const value = draft.parts[part];
  const sub =
    part === "state"
      ? stateNameOf(settings?.locationAddress.state ?? "")
      : optional
        ? "Optional"
        : "";
  const row = el(
    "div",
    { cls: "ow-trow", testid: "ow-tax-part", attrs: { "data-part": part } },
    el(
      "div",
      { cls: "ow-trow-name" },
      el("span", { text: label }),
      ...(sub === "" ? [] : [el("small", { text: sub })]),
    ),
  );
  if (value === null) {
    const none = el("span", {
      cls: "ow-stepval ow-stepval-none",
      text: "Not used",
      testid: "ow-tax-part-value",
    });
    const add = button("Add", "ow-tax-part-add", "ow-btn", () => {
      taxDraft = setTaxPart(draft, part, 0);
      taxConfirmedNow = false;
      render();
    });
    add.disabled = locked;
    row.append(none, add);
    return row;
  }
  const control = stepper({
    testid: `ow-tax-${part}`,
    value: percentText(value),
    minusLabel: `Less ${label}`,
    plusLabel: `More ${label}`,
    minusDisabled: locked || value <= 0,
    plusDisabled: locked || value >= MAX_TAX_BASIS_POINTS,
    onMinus: () => {
      taxDraft = stepTaxPart(draft, part, -1);
      taxConfirmedNow = false;
      render();
    },
    onPlus: () => {
      taxDraft = stepTaxPart(draft, part, 1);
      taxConfirmedNow = false;
      render();
    },
    ...(locked
      ? {}
      : {
          onValue: () => {
            void openPadSheet({
              title: `${label} sales tax`,
              initial: value,
              max: MAX_TAX_BASIS_POINTS,
              format: percentText,
            }).then((picked) => {
              if (picked === undefined || session === undefined || !open) return;
              taxDraft = setTaxPart(draft, part, picked);
              taxConfirmedNow = false;
              render();
            });
          },
        }),
  });
  row.append(control);
  if (optional && !locked) {
    row.append(
      button("Remove", "ow-tax-part-remove", "ow-btn ow-btn-quiet", () => {
        taxDraft = setTaxPart(draft, part, null);
        taxConfirmedNow = false;
        render();
      }),
    );
  }
  return row;
}

/** The rate already in force, as its parts, when the tax was confirmed before this screen opened. */
async function loadStoredRate(active: Session): Promise<void> {
  const locationId = requireDeps().deviceLocationId();
  const result = await getTaxRates(active.apiBaseUrl, active.tenantId, locationId);
  if (!live(active.token) || result.status !== 200 || taxDraft === undefined) return;
  const inEffect = result.body.inEffect;
  // Only a rate someone saved for the location counts. The tenant-wide fallback is a bootstrap value, not a choice.
  if (inEffect === null || inEffect.source !== "rate_table") return;
  const parts: Record<TaxPartId, number | null> = {
    state: null,
    county: null,
    city: null,
    special_district: null,
  };
  let named = false;
  for (const entry of inEffect.components) {
    if (
      entry.component === "state" ||
      entry.component === "county" ||
      entry.component === "city" ||
      entry.component === "special_district"
    ) {
      parts[entry.component] = entry.basisPoints;
      named = true;
    }
  }
  if (
    screen.kind === "step" &&
    steps()[screen.index]?.id === "tax" &&
    !taxEditing &&
    !taxDraft.edited
  ) {
    taxDraft = named
      ? { mode: "parts", parts, single: null, edited: false }
      : { mode: "single", parts, single: inEffect.totalBasisPoints, edited: false };
    taxFromSaved = !taxConfirmedNow;
    render();
  }
}

async function useTaxRate(): Promise<void> {
  const active = session;
  const draft = taxDraft;
  const current = settings;
  if (active === undefined || draft === undefined || current === undefined) return;
  await runBusy(async () => {
    const locationId = requireDeps().deviceLocationId();
    const result = await putTaxRates(active.apiBaseUrl, active.tenantId, {
      ...(locationId === undefined ? {} : { locationId: locationId }),
      effectiveFrom: todayIn(current.businessDayTimezone),
      components: taxComponentsToRecord(draft),
      idempotencyKey: crypto.randomUUID(),
    });
    if (!live(active.token)) return;
    if (result.status !== 200 && result.status !== 201) {
      showReason(result.body.message ?? result.body.error ?? "Could not save the rate");
      return;
    }
    // Recording the rate set is the owner's deliberate answer: the same call confirms it (ONB1), which the checklist reads.
    taxConfirmedNow = true;
    taxEditing = false;
    await refreshReads(active);
    if (live(active.token)) render();
  });
}

// ---- 5. What tips do guests see -------------------------------------------------------------------

function renderTips(step: OwnerStepDef): void {
  const active = session;
  const current = settings;
  if (active === undefined || current === undefined) return;
  if (tipDraft === undefined) {
    // The one tip setting (DEC-541): the step starts from what is saved for this location, and from the default only when
    // nothing is saved.
    tipDraft =
      current.tipPresetsBasisPoints.length > 0
        ? [...current.tipPresetsBasisPoints].sort((a, b) => a - b)
        : [...DEFAULT_TIP_BASIS_POINTS];
    tipsChanging = false;
  }
  const values = tipDraft;
  const col = column();
  heading(col, kickerFor(step), step.title, "Guests see these on the tablet Pay screen and the card reader.");
  const tiles = el("div", { cls: "ow-tiles", testid: "ow-tips" });
  values.forEach((value, i) => {
    const tile = el("button", {
      cls: tipsChanging ? "ow-tile ow-tile-edit" : "ow-tile",
      text: tipText(value),
      testid: "ow-tip",
      attrs: { type: "button", "data-index": String(i) },
    });
    if (tipsChanging) {
      tile.addEventListener("click", () => {
        void openPadSheet({
          title: `Tip choice ${i + 1}`,
          initial: value,
          max: MAX_TIP_BASIS_POINTS,
          format: percentText,
        }).then((picked) => {
          if (picked === undefined || !open || session === undefined) return;
          values[i] = picked;
          render();
        });
      });
    } else {
      tile.disabled = true;
    }
    tiles.append(tile);
  });
  col.append(card("ow-tips-card", el("div", { cls: "ow-label", text: "Guest sees" }), tiles));
  if (tipsChanging) {
    col.append(el("p", { cls: "ow-sub", text: "Tap a tip to change it", testid: "ow-tips-hint" }));
  }
  if (!tipsChanging) {
    col.append(
      el(
        "div",
        { cls: "ow-stack" },
        button("Change these", "ow-tips-change", "ow-btn", () => {
          tipsChanging = true;
          render();
        }),
      ),
    );
  }
  const reason = tipsBlockedReason(values);
  const writable = canManageSettings(active);
  setPrimary(
    "Looks good",
    writable && reason === "",
    writable ? reason : "Not allowed",
    () => void saveTips(),
  );
}

async function saveTips(): Promise<void> {
  const active = session;
  const values = tipDraft;
  if (active === undefined || values === undefined) return;
  await runBusy(async () => {
    const result = await updateSettings(active.apiBaseUrl, active.tenantId, {
      tipPresetsBasisPoints: [...values].sort((a, b) => a - b),
    });
    if (!live(active.token)) return;
    if (result.status !== 200) {
      showReason(result.body.message ?? result.body.error ?? "Could not save the tips");
      return;
    }
    settings = result.body.settings;
    tipDraft = undefined;
    if (!(await commit({ review: ["tips"], unskip: ["tips"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

// ---- 6. How do you want to add your bottles -------------------------------------------------------

async function loadVocab(active: Session): Promise<void> {
  if (Object.keys(vocab).length > 0) return;
  const result = await getVocabularies(active.apiBaseUrl, active.tenantId);
  if (!live(active.token)) return;
  if (result.status === 200) vocab = result.body.vocabularies;
}

async function loadBottles(active: Session): Promise<void> {
  const result = await listLiquorProducts(active.apiBaseUrl, active.tenantId, { activeOnly: true });
  if (!live(active.token)) return;
  if (result.status === 200) bottles = result.body.liquorProducts;
}

function liquorCategories(): Array<{ value: string; label: string }> {
  return (vocab["item_category"] ?? []).filter((entry) => entry.itemKindScope === "liquor")
    .filter((entry) => entry.active)
    .map((entry) => ({ value: entry.value, label: entry.label }));
}

function renderBottles(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  if (Object.keys(vocab).length === 0)
    void loadVocab(active).then(() => live(active.token) && bottleFormOpen && render());
  if (bottleFormOpen) {
    renderBottleForm(step);
    return;
  }
  const done = checklistStates["bottles"] === "done";
  const col = column();
  heading(col, kickerFor(step), step.title, "Pick one. You can change it later.");
  const choices: Array<{ way: BottleWayChoice; title: string; glyph: string }> = [
    { way: "upload", title: "Upload my list", glyph: "↑" },
    { way: "hand", title: "Add them here, one by one", glyph: "+" },
    { way: "later", title: "Do this later", glyph: "→" },
  ];
  const group = el("div", {
    cls: "ow-picks",
    attrs: { role: "radiogroup", "aria-label": "How to add bottles" },
  });
  for (const choice of choices) {
    group.append(
      pickCard({
        testid: "ow-bottles-way",
        title: choice.title,
        chosen: bottleWay === choice.way,
        glyph: choice.glyph,
        onPick: () => {
          bottleWay = choice.way;
          render();
        },
      }),
    );
    group.lastElementChild?.setAttribute("data-way", choice.way);
  }
  col.append(group);
  const canAdd = hasPermission(active, "admin.liquor_product.manage");
  const label = "Continue";
  const ready = bottleWay !== undefined || done;
  const reason =
    bottleWay === undefined && !done
      ? "Pick one to continue"
      : !canAdd && bottleWay !== "later" && bottleWay !== undefined
        ? "Not allowed"
        : "";
  setPrimary(label, ready && reason === "", reason, () => void continueBottles());
}

async function continueBottles(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  const way = bottleWay;
  if (way === "later") {
    await skipStep();
    return;
  }
  if (way === "hand") {
    bottleFormOpen = true;
    if (pendingBottleCreate === undefined) {
      bottleCreateSellingRequestId = crypto.randomUUID();
      bottleDraft = { name: "", category: "", size: "", shelfTier: "" };
    }
    render();
    return;
  }
  if (way === "upload") {
    openInventoryImport({
      ...(wizardScreenEl.dataset["theme"] === undefined ? {} : { theme: wizardScreenEl.dataset["theme"] }),
      getSession: () => deps?.getSession(),
      getPlanningLocationId: () => requireDeps().deviceLocationId(),
      onClose: (imported) => {
        if (imported === undefined || !live(active.token)) return;
        void (async () => {
          await refreshReads(active);
          if (!live(active.token)) return;
          await continueStep(["bottles"], { refresh: false });
        })();
      },
    });
    return;
  }
  // Nothing chosen but the bottles are already there: on to the next step.
  await continueStep(["bottles"]);
}

function renderBottleForm(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const draft = bottleDraft;
  const col = column();
  heading(
    col,
    kickerFor(step),
    "Add a bottle",
    addedBottles.length === 0 ? "Name it, pick its type and size, set your price." : undefined,
  );

  if (addedBottles.length > 0) {
    const list = el("div", { cls: "ow-list", testid: "ow-bottles-added" });
    for (const bottle of addedBottles) {
      list.append(
        el(
          "div",
          { cls: "ow-li", testid: "ow-bottle-added" },
          el("div", { cls: "ow-li-main" }, el("div", { cls: "ow-li-title", text: bottle.name })),
          tag("Added", "ok"),
        ),
      );
    }
    col.append(list);
  }

  const name = textInput({
    id: "ow-bottle-name",
    testid: "ow-bottle-name",
    value: draft.name,
    placeholder: "Bottle name",
  });
  name.addEventListener("input", () => {
    draft.name = name.value;
    refresh();
  });
  const typeChips = chipRow(
    "ow-bottle-types",
    "ow-bottle-type",
    liquorCategories(),
    draft.category,
    (value) => {
      draft.category = value;
      markChosen(typeChips, value);
      refresh();
    },
    "Type",
  );
  const sizeChips = chipRow(
    "ow-bottle-sizes",
    "ow-bottle-size",
    BOTTLE_SIZES.map((size) => ({ value: size.value, label: size.label })),
    draft.size,
    (value) => {
      draft.size = value;
      markChosen(sizeChips, value);
      refresh();
    },
    "Size",
  );
  const pricing = el("div", { cls: "ow-field" });
  if (settings?.pricingModel === "shelf") {
    const tierSelect = el("select", { cls: "ow-input", testid: "ow-bottle-shelf-tier", attrs: { "aria-label": "Shelf tier" } });
    tierSelect.append(el("option", { text: "Choose a shelf tier", attrs: { value: "" } }));
    const tiers = (vocab["shelf_tier"] ?? []).filter((entry) => entry.active);
    // SHELFNAME1: show the location's own shelf names (Guest, Member...), not the Tier 1 to Tier 4 keys.
    const shelfName = (value: string, fallback: string): string => settings?.shelfTiers.find((entry) => entry.tier === value)?.name || fallback;
    for (const tier of tiers) tierSelect.append(el("option", { text: shelfName(tier.value, tier.label), attrs: { value: tier.value } }));
    tierSelect.value = draft.shelfTier ?? "";
    const tierPrice = el("span", { cls: "ow-label" });
    const showTier = (): void => {
      const selected = tiers.find((entry) => entry.value === tierSelect.value);
      const price = setupSelling.context?.readiness.state === "preparation" ? selected?.priceCents :
        setupSelling.context?.readiness.state === "enforced" && setupSelling.siteId === requireDeps().deviceLocationId() ? setupSelling.shelves.get(tierSelect.value)?.priceCents : null;
      tierPrice.textContent = price == null ? "Price unavailable" : dollarText(price);
    };
    tierSelect.addEventListener("change", () => { draft.shelfTier = tierSelect.value; showTier(); refresh(); });
    showTier();
    pricing.append(el("span", { cls: "ow-label", text: "Shelf tier" }), tierSelect, tierPrice);
  } else {
    pricing.append(el("span", { cls: "ow-label", text: "Cost unavailable" }), el("span", { cls: "ow-label", text: "Price unavailable" }));
  }
  col.append(
    el(
      "div",
      { cls: "ow-stack" },
      field("Name", name),
      el("div", { cls: "ow-field" }, el("span", { cls: "ow-label", text: "Type" }), typeChips),
      el("div", { cls: "ow-field" }, el("span", { cls: "ow-label", text: "Size" }), sizeChips),
      pricing,
    ),
  );
  setBack(() => {
    bottleFormOpen = false;
    render();
  });
  if (addedBottles.length > 0) {
    setSecondary("Done adding", () => {
      bottleFormOpen = false;
      void continueStep(["bottles"]);
    });
  }
  const refresh = (): void => {
    const reason = bottleBlockedReason(draft) || (settings?.pricingModel === "shelf" && !draft.shelfTier ? "Choose a shelf tier" : "");
    setPrimary("Add bottle", reason === "", reason, () => void addBottle());
  };
  refresh();
}

async function addBottle(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  const draft = { ...bottleDraft };
  const sellingSiteId = requireDeps().deviceLocationId();
  const createRequestId = bottleCreateSellingRequestId;
  await runBusy(async () => {
    const size = BOTTLE_SIZES.find((entry) => entry.value === draft.size);
    if (size === undefined) return;
    const body: Parameters<typeof createLiquorProduct>[2] = {
      ...(sellingSiteId === undefined
        ? {}
        : { locationId: sellingSiteId, sellingRequestId: createRequestId, sellingVersion: 0 }),
      name: draft.name.trim(),
      category: draft.category,
      itemKind: "liquor",
      ...(draft.shelfTier ? { shelfTier: draft.shelfTier } : {}),
      sizeValue: size.sizeValue,
      sizeUnit: size.sizeUnit,
      containerType: "bottle",
    };
    if (pendingBottleCreate && (pendingBottleCreate.session !== active || pendingBottleCreate.siteId !== sellingSiteId || JSON.stringify(pendingBottleCreate.body) !== JSON.stringify(body))) {
      showReason("Previous creation unconfirmed. Restore the original bottle details and location to retry.");
      return;
    }
    const original = pendingBottleCreate ?? { session: active, siteId: sellingSiteId, body: structuredClone(body), uncertain: false };
    pendingBottleCreate = original;
    let result: Awaited<ReturnType<typeof createLiquorProduct>>;
    try {
      result = await createLiquorProduct(active.apiBaseUrl, active.tenantId, original.body);
    } catch {
      original.uncertain = true;
      if (live(active.token) && session === active && requireDeps().deviceLocationId() === sellingSiteId) showReason("Creation unconfirmed. Retry the original bottle details.");
      return;
    }
    if (!live(active.token) || session !== active || requireDeps().deviceLocationId() !== sellingSiteId) return;
    if (result.networkError || result.status >= 500) original.uncertain = true;
    if (result.status !== 201) {
      if (!original.uncertain) pendingBottleCreate = undefined;
      showReason(result.body.message ?? result.body.error ?? "Could not add the bottle");
      return;
    }
    pendingBottleCreate = undefined;
    bottleCreateSellingRequestId = crypto.randomUUID();
    addedBottles = [...addedBottles, result.body.liquorProduct];
    bottleDraft = { name: "", category: "", size: "", shelfTier: "" };
    await refreshReads(active);
    if (live(active.token)) render();
  });
}

// ---- 7. Build your first drink --------------------------------------------------------------------

function renderFirstDrink(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  if (bottles.length === 0) {
    void loadBottles(active).then(() => {
      if (
        live(active.token) &&
        screen.kind === "step" &&
        steps()[screen.index]?.id === "firstDrink" &&
        bottles.length > 0
      )
        render();
    });
  }
  const draft = drinkDraft;
  const col = column(true);
  heading(col, kickerFor(step), step.title, "The price comes from the ounces you pour.");
  const left = el("div", { cls: "ow-drink-left" });
  const starters = chipRow(
    "ow-drink-starters",
    "ow-drink-starter",
    DRINK_STARTERS.map((entry) => ({ value: entry.id, label: entry.name })),
    draft.starterId,
    (value) => {
      draft.starterId = value;
      render();
    },
    "Drink",
  );
  left.append(
    el("div", { cls: "ow-field" }, el("span", { cls: "ow-label", text: "Drink" }), starters),
  );
  if (draft.starterId === "other") {
    const other = textInput({
      id: "ow-drink-name",
      testid: "ow-drink-name",
      value: draft.otherName,
      placeholder: "Drink name",
    });
    other.addEventListener("input", () => {
      draft.otherName = other.value;
      refresh();
    });
    left.append(field("Name", other));
  }
  const chosen = bottles.find((bottle) => bottle.id === draft.liquorProductId);
  const bottleCard = el("div", { cls: "ow-bottlecard", testid: "ow-drink-bottle-row" });
  if (chosen === undefined) {
    bottleCard.append(
      el("div", { cls: "ow-li-title", text: "Choose a bottle" }),
      button("Pick", "ow-drink-pick", "ow-btn", () => void pickBottle()),
    );
    bottleCard.classList.add("ow-bottlecard-empty");
  } else {
    bottleCard.append(
      el("div", { cls: "ow-li-title", text: chosen.name, testid: "ow-drink-bottle-name" }),
      chipRow(
        "ow-drink-ounces",
        "ow-drink-oz",
        DRINK_POUR_CHOICES.map((oz) => ({ value: String(oz), label: ouncesText(oz) })),
        draft.ounces === null ? "" : String(draft.ounces),
        (value) => {
          draft.ounces = Number(value);
          render();
        },
        "Ounces",
      ),
    );
    bottleCard.append(
      button("Change", "ow-drink-change", "ow-btn ow-btn-quiet", () => void pickBottle()),
    );
  }
  left.append(
    el(
      "div",
      { cls: "ow-field" },
      el("span", { cls: "ow-label", text: "Bottle in it" }),
      bottleCard,
    ),
  );

  const shelfPricing = settings?.pricingModel === "shelf";
  const view = shelfPricing ? undefined : drinkPriceView({
    bottleName: chosen?.category === undefined ? chosen?.name : categoryLabel(chosen.category),
    perOunceCents: chosen?.per_ounce_price_cents ?? undefined,
    draft,
  });
  const priceCard = el("div", { cls: "ow-card ow-price", testid: "ow-price-card" });
  priceCard.append(el("div", { cls: "ow-label", text: "Price guests pay" }));
  priceCard.append(
    el("div", {
      cls: "ow-hugevalue",
      text: view === undefined ? "Price unavailable" : dollarText(view.totalCents),
      testid: "ow-price-total",
    }),
  );
  if (view !== undefined) {
    priceCard.append(priceLine("ow-price-pour", view.pourLine, dollarText(view.pourCents)));
  }
  const extra = el("div", { cls: "ow-priceline" }, el("span", { text: "Build charge" }));
  const extraStep = stepper({
    testid: "ow-drink-extra",
    value: dollarText(draft.buildChargeCents),
    minusLabel: "Less build charge",
    plusLabel: "More build charge",
    minusDisabled: draft.buildChargeCents <= 0,
    onMinus: () => {
      draft.buildChargeCents = Math.max(0, draft.buildChargeCents - 50);
      render();
    },
    onPlus: () => {
      draft.buildChargeCents = Math.min(MAX_PRICE_CENTS, draft.buildChargeCents + 50);
      render();
    },
    onValue: () => {
      void openPadSheet({
        title: "Build charge",
        initial: draft.buildChargeCents,
        max: MAX_PRICE_CENTS,
        format: dollarText,
      }).then((picked) => {
        if (picked === undefined || !open || session === undefined) return;
        draft.buildChargeCents = picked;
        render();
      });
    },
  });
  extra.append(extraStep);
  // DEC-607: under shelf pricing the build charge is added to the shelf price.
  priceCard.append(extra);
  if (shelfPricing && chosen !== undefined && draft.ounces !== null) {
    const selectedOunces = draft.ounces;
    void quoteInventorySale(active.apiBaseUrl, active.tenantId, { saleKind: "pour", liquorProductId: chosen.id, ounces: String(selectedOunces) })
      .then((quote) => {
        if (!live(active.token) || !priceCard.isConnected || drinkDraft.liquorProductId !== chosen.id || drinkDraft.ounces !== selectedOunces) return;
        const value = priceCard.querySelector<HTMLElement>("[data-testid=ow-price-total]");
        if (value !== null) value.textContent = quote.status === 200 ? dollarText(quote.body.lineTotalCents + draft.buildChargeCents) : "Price unavailable";
      });
  }
  const cols = el("div", { cls: "ow-cols" }, left, el("div", { cls: "ow-drink-right" }, priceCard));
  col.append(cols);
  void active;
  const refresh = (): void => {
    const reason = drinkBlockedReason(draft);
    const allowed = hasPermission(session as Session, "admin.menu_drink.manage");
    setPrimary(
      "Save drink",
      reason === "" && allowed,
      allowed ? reason : "Not allowed",
      () => void saveDrink(),
    );
  };
  refresh();
  setSecondary("Skip for now", () => void skipStep());
}

function categoryLabel(value: string): string {
  return (vocab["item_category"] ?? []).filter((entry) => entry.itemKindScope === "liquor").find((entry) => entry.value === value)?.label ?? value;
}

function priceLine(testid: string, label: string, value: string): HTMLElement {
  return el(
    "div",
    { cls: "ow-priceline", testid },
    el("span", { text: label }),
    el("b", { text: value }),
  );
}

function pickBottle(): Promise<void> {
  return new Promise((resolve) => {
    const host = document.querySelector<HTMLElement>("#setup-wizard-screen");
    if (host === null || session === undefined) {
      resolve();
      return;
    }
    const scrim = el("div", { cls: "ow-scrim", testid: "ow-bottle-sheet" });
    const close = (): void => {
      scrim.remove();
      resolve();
    };
    const list = el("div", { cls: "ow-list ow-sheet-list" });
    for (const bottle of bottles) {
      const row = el(
        "button",
        {
          cls: "ow-li ow-li-button",
          testid: "ow-bottle-option",
          attrs: { type: "button", "data-id": bottle.id },
        },
        el(
          "div",
          { cls: "ow-li-main" },
          el("div", { cls: "ow-li-title", text: bottle.name }),
          el("div", { cls: "ow-li-sub", text: categoryLabel(bottle.category) }),
        ),
        el("span", {
          cls: "ow-li-value",
          text:
            settings?.pricingModel === "shelf"
              ? (vocab["shelf_tier"] ?? []).find((tier) => tier.value === bottle.shelf_tier)?.label ?? "Price unavailable"
              : bottle.per_ounce_price_cents === null ? "Price unavailable" : `${dollarText(bottle.per_ounce_price_cents)} / oz`,
        }),
      );
      row.addEventListener("click", () => {
        drinkDraft.liquorProductId = bottle.id;
        close();
        render();
      });
      list.append(row);
    }
    const cancel = button("Cancel", "ow-bottle-cancel", "ow-btn", close);
    scrim.append(
      el(
        "div",
        {
          cls: "ow-sheet ow-sheet-tall",
          attrs: { role: "dialog", "aria-modal": "true", "aria-label": "Choose a bottle" },
        },
        el("div", { cls: "ow-sheet-title", text: "Choose a bottle" }),
        list,
        el("div", { cls: "ow-sheet-actions" }, cancel),
      ),
    );
    host.append(scrim);
  });
}

async function saveDrink(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  const draft = { ...drinkDraft };
  await runBusy(async () => {
    const bottle = bottles.find((entry) => entry.id === draft.liquorProductId);
    if (bottle === undefined || draft.ounces === null) return;
    await loadVocab(active);
    const menuCategories = (vocab["drink_display_category"] ?? [])
      .filter((entry) => entry.active)
      .map((entry) => entry.value);
    // The drink takes a liquor chosen at the location each time (no default): the bottle picked here sets the kind of liquor
    // and the ounces of the pour, and the price is the ounces times the chosen bottle's price per ounce, plus the build charge.
    const result = await createMenuDrink(active.apiBaseUrl, active.tenantId, {
      name: drinkName(draft),
      category: drinkCategoryFor(bottle.category, menuCategories),
      buildChargeCents: draft.buildChargeCents,
      requiresLiquorSelection: true,
      liquorSlots: [bottle.category],
      components: [
        {
          componentReference: categoryLabel(bottle.category),
          quantity: draft.ounces,
          unit: "oz",
          isLiquorSlot: true,
        },
      ],
    });
    if (!live(active.token)) return;
    if (result.status !== 201) {
      showReason(result.body.message ?? result.body.error ?? "Could not save the drink");
      return;
    }
    drinkDraft = emptyDrinkDraft();
    if (!(await commit({ unskip: ["firstDrink"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

// ---- 8. How many bottles do you have --------------------------------------------------------------

function countableBottles(): StockItem[] {
  return bottles
    .map((bottle) => classifyItem(bottle))
    .filter((item) => item.kind === "bottle" || item.kind === "keg" || item.kind === "unit");
}

function renderStock(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  if (bottles.length === 0) {
    void loadBottles(active).then(() => {
      if (
        live(active.token) &&
        screen.kind === "step" &&
        steps()[screen.index]?.id === "stock" &&
        bottles.length > 0
      )
        render();
    });
  }
  const col = column();
  heading(col, kickerFor(step), step.title, "Count what is on the shelf today.");
  const items = countableBottles();
  if (items.length > 0) {
    const list = el("div", { cls: "ow-list", testid: "ow-stock-list" });
    for (const item of items) {
      const count = stockCounts.get(item.id) ?? 0;
      list.append(
        el(
          "div",
          { cls: "ow-li", testid: "ow-stock-row", attrs: { "data-id": item.id } },
          el("div", { cls: "ow-li-main" }, el("div", { cls: "ow-li-title", text: item.name })),
          stepper({
            testid: "ow-stock",
            value: String(count),
            minusLabel: `Fewer ${item.name}`,
            plusLabel: `More ${item.name}`,
            minusDisabled: count <= 0,
            plusDisabled: count >= MAX_BOTTLES,
            onMinus: () => {
              stockCounts.set(item.id, stepBottleCount(count, -1));
              render();
            },
            onPlus: () => {
              stockCounts.set(item.id, stepBottleCount(count, 1));
              render();
            },
          }),
        ),
      );
    }
    col.append(list);
  }
  col.append(
    el(
      "div",
      { cls: "ow-stack" },
      button("This location starts empty", "ow-stock-empty", "ow-btn", () => void startsEmpty()),
    ),
  );
  const canRecord = hasPermission(active, "inv.movement.record");
  const reason = !canRecord ? "Not allowed" : items.length === 0 ? "Add a bottle first" : "";
  setPrimary("Continue", canRecord && items.length > 0, reason, () => void saveStock(items));
  setSecondary("Skip for now", () => void skipStep());
}

async function startsEmpty(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    const result = await setStartsEmpty(active.apiBaseUrl, active.tenantId, true);
    if (!live(active.token)) return;
    if (result.status !== 200) {
      showReason(result.body.message ?? result.body.error ?? "Could not save that");
      return;
    }
    if (!(await commit({ unskip: ["stock"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

async function saveStock(items: readonly StockItem[]): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    const batch = crypto.randomUUID();
    for (const item of items) {
      const count = stockCounts.get(item.id) ?? 0;
      let entry: Entry;
      if (count === 0) entry = { type: "zero" };
      else if (item.kind === "unit") entry = { type: "units", units: count };
      else entry = { type: "container", whole: count, tenth: 0 };
      const body = buildRequest(item, entry, batch);
      const result = await recordOpeningBalanceMovement(active.apiBaseUrl, active.tenantId, body);
      if (!live(active.token)) return;
      if (result.status !== 200 && result.status !== 201) {
        showReason(result.body.message ?? result.body.error ?? `Could not save ${item.name}`);
        return;
      }
    }
    if (!(await commit({ unskip: ["stock"] }))) return;
    await refreshReads(active);
    if (!live(active.token)) return;
    await goNext();
  });
}

// ---- 9. Who works at your location ---------------------------------------------------------------------

async function loadPeople(active: Session): Promise<void> {
  const [roleResult, userResult] = await Promise.all([
    hasPermission(active, "admin.user.create")
      ? listTenantRoles(active.apiBaseUrl, active.tenantId)
      : undefined,
    hasPermission(active, "admin.user.view")
      ? listUsersForAdmin(active.apiBaseUrl, active.tenantId, { activeOnly: false })
      : undefined,
  ]);
  if (!live(active.token)) return;
  if (roleResult?.status === 200) roles = roleResult.body.roles;
  if (userResult?.status === 200) users = userResult.body.users;
}

function renderTeam(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  if (roles.length === 0 && users.length === 0) {
    void loadPeople(active).then(() => {
      if (
        live(active.token) &&
        screen.kind === "step" &&
        steps()[screen.index]?.id === "team" &&
        (roles.length > 0 || users.length > 0)
      )
        render();
    });
  }
  if (teamFormOpen) {
    renderPersonForm(step);
    return;
  }
  const col = column();
  heading(col, kickerFor(step), step.title, "You are already on the list.");
  const list = el("div", { cls: "ow-list", testid: "ow-people" });
  list.append(personRow(active.employeeId, "You", "ow-tag-todo", true));
  for (const person of users.filter((user) => user.active && user.id !== active.userId)) {
    const label = roles.find((role) => role.role === person.role)?.label ?? person.role;
    list.append(personRow(person.displayName, label, "ow-tag-ok", false));
  }
  col.append(list);
  const canAdd = hasPermission(active, "admin.user.create");
  if (canAdd) {
    col.append(
      el(
        "div",
        { cls: "ow-stack" },
        button("+ Add a person", "ow-person-add", "ow-btn ow-btn-wide", () => {
          teamFormOpen = true;
          personDraft = { name: "", role: "", pin: "" };
          render();
        }),
      ),
    );
  }
  setPrimary("Continue", true, "", () => void continueStep(["team"]));
  setSecondary("Add people later", () => void skipStep());
}

function personRow(name: string, sub: string, tagCls: string, self: boolean): HTMLElement {
  return el(
    "div",
    { cls: "ow-li", testid: "ow-person", attrs: { "data-self": self ? "true" : "false" } },
    el("span", { cls: "ow-avatar", text: initialsOf(name) }),
    el(
      "div",
      { cls: "ow-li-main" },
      el("div", { cls: "ow-li-title", text: name }),
      el("div", { cls: "ow-li-sub", text: self ? "Owner" : sub }),
    ),
    el("span", { cls: `ow-tag ${tagCls}`, text: self ? "You" : "Added" }),
  );
}

function renderPersonForm(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const minLength = active.pinMinLength;
  const maxLength = active.pinMaxLength;
  const col = column(true);
  heading(col, kickerFor(step), "Add a person");
  const nameInput = textInput({
    id: "ow-person-name",
    testid: "ow-person-name",
    value: personDraft.name,
    placeholder: "Name",
  });
  const chips = chipRow(
    "ow-role-chips",
    "ow-role-chip",
    roles
      .filter((role) => role.active && !role.isSystem)
      .map((role) => ({ value: role.role, label: role.label })),
    personDraft.role,
    (value) => {
      personDraft.role = value;
      markChosen(chips, value);
      refresh();
    },
    "Job",
  );
  const dots = el("div", { cls: "ow-dots", testid: "ow-pin-dots" });
  const error = el("div", { cls: "ow-error", testid: "ow-person-error", attrs: { role: "alert" } });
  nameInput.addEventListener("input", () => {
    personDraft.name = nameInput.value;
    refresh();
  });
  const left = el(
    "div",
    { cls: "ow-stack" },
    field("Name", nameInput),
    el("div", { cls: "ow-field" }, el("span", { cls: "ow-label", text: "Job" }), chips),
    error,
  );
  const pad = el("div", { cls: "ow-keys", testid: "ow-pin-pad" });
  const digit = (value: string): HTMLButtonElement => {
    const node = el("button", {
      cls: "ow-key",
      text: value,
      testid: `ow-pin-digit-${value}`,
      attrs: { type: "button" },
    });
    node.addEventListener("click", () => {
      personDraft.pin = pinAppend(personDraft.pin, value, maxLength);
      refresh();
    });
    return node;
  };
  const fn = (label: string, testid: string, onTap: () => void): HTMLButtonElement => {
    const node = el("button", {
      cls: "ow-key ow-key-fn",
      text: label,
      testid,
      attrs: { type: "button" },
    });
    node.addEventListener("click", onTap);
    return node;
  };
  for (const value of ["1", "2", "3", "4", "5", "6", "7", "8", "9"]) pad.append(digit(value));
  pad.append(
    fn("Clear", "ow-pin-clear", () => {
      personDraft.pin = "";
      refresh();
    }),
    digit("0"),
    fn("Delete", "ow-pin-delete", () => {
      personDraft.pin = pinBackspace(personDraft.pin);
      refresh();
    }),
  );
  // PINKEYS1 (owner rule: a physical keyboard always works alongside touch): while this pad is on screen, typed digits
  // and Backspace fill the PIN, unless the cursor is in a text field such as Name. The listener removes itself once the
  // pad leaves the page.
  const onPadKey = (event: KeyboardEvent): void => {
    if (!pad.isConnected) {
      document.removeEventListener("keydown", onPadKey);
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLInputElement && !["button", "checkbox", "radio", "submit", "reset"].includes(target.type)))
    )
      return;
    if (/^[0-9]$/.test(event.key)) personDraft.pin = pinAppend(personDraft.pin, event.key, maxLength);
    else if (event.key === "Backspace") personDraft.pin = pinBackspace(personDraft.pin);
    else return;
    event.preventDefault();
    refresh();
  };
  document.addEventListener("keydown", onPadKey);
  const right = card(
    "ow-pin-card",
    el("div", { cls: "ow-label", text: `PIN, ${pinLengthRangeText(minLength, maxLength)}` }),
    dots,
    pad,
  );
  col.append(el("div", { cls: "ow-cols" }, left, right));
  setBack(() => {
    teamFormOpen = false;
    render();
  });
  const refresh = (): void => {
    dots.replaceChildren();
    const count = pinDotCount(personDraft.pin, minLength, maxLength);
    for (let i = 0; i < count; i += 1) {
      dots.append(
        el("i", { attrs: { "data-filled": i < personDraft.pin.length ? "true" : "false" } }),
      );
    }
    const view = personFormView(personDraft, minLength);
    setPrimary("Add person", view.canAdd, view.blockedReason, () => void addPerson());
  };
  refresh();
}

async function addPerson(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  const draft = { ...personDraft };
  await runBusy(async () => {
    const result = await createUser(active.apiBaseUrl, active.tenantId, {
      idempotencyKey: crypto.randomUUID(),
      displayName: draft.name.trim(),
      role: draft.role,
      chosenPin: draft.pin,
    });
    if (!live(active.token)) return;
    if (result.status !== 201 && result.status !== 200) {
      const text = result.body.message ?? result.body.error ?? "Could not add that person";
      const errorEl = bodyEl.querySelector<HTMLElement>('[data-testid="ow-person-error"]');
      if (errorEl !== null) errorEl.textContent = text;
      return;
    }
    personDraft = { name: "", role: "", pin: "" };
    teamFormOpen = false;
    const userResult = await listUsersForAdmin(active.apiBaseUrl, active.tenantId, {
      activeOnly: false,
    });
    if (!live(active.token)) return;
    if (userResult.status === 200) users = userResult.body.users;
    await refreshReads(active);
    if (live(active.token)) render();
  });
}

// ---- 10. Card account -----------------------------------------------------------------------------

function cardNeedsAccount(): boolean {
  return cardLive() && cardPhase() !== "on";
}

function renderCardAccount(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const here = cardView?.thisLocation;
  const phase = cardPhase();
  const view = cardPaymentsStepView({
    phase,
    requirements: here?.requirements ?? [],
    reasonLabel: here?.reasonLabel ?? null,
    masked: here?.masked ?? null,
    canOnboard: hasPermission(active, "admin.payments.onboard"),
    review: here === undefined ? undefined : reviewStateOf(here, new Date()),
  });
  scheduleCardPoll();
  const col = column();
  heading(
    col,
    kickerFor(step),
    phase === "waiting" ? step.title : view.title,
    phase === "waiting"
      ? "We are creating your card account. This usually takes a minute."
      : undefined,
  );
  const status = el("div", {
    cls: "ow-card",
    testid: "setup-wizard-card-payments",
    attrs: { "data-phase": phase },
  });
  const tagLabel = phase === "waiting" ? "Being created" : view.tag.label;
  status.append(
    el(
      "div",
      { cls: "ow-keyrow" },
      el("span", { text: "Your account" }),
      tag(tagLabel, view.tag.ok ? "ok" : "warn", "setup-wizard-card-tag"),
    ),
  );
  for (const row of view.rows) {
    if (phase === "waiting") continue;
    status.append(
      el(
        "div",
        { cls: "ow-keyrow", testid: "setup-wizard-card-row" },
        el("span", { text: row.label }),
        el("b", { text: row.value }),
      ),
    );
  }
  if (phase === "waiting") {
    status.append(
      el(
        "div",
        { cls: "ow-keyrow", testid: "setup-wizard-card-row" },
        el("span", { text: "Needed from you" }),
        el("b", { text: "Nothing" }),
      ),
      el("p", {
        cls: "ow-hint",
        testid: "setup-wizard-card-waiting-note",
        text: "If it takes longer, Snip & Sip has been told and is finishing a few go-live items. You can go on and come back.",
      }),
    );
  }
  status.append(
    el("div", { cls: "ow-error", testid: "setup-wizard-card-error", attrs: { role: "alert" } }),
  );
  col.append(status);
  if (view.checkAgain) {
    col.append(
      el(
        "div",
        { cls: "ow-stack" },
        button(
          "Check again",
          "setup-wizard-card-check",
          "ow-btn ow-btn-wide",
          () => void checkCardPaymentsAgain(),
        ),
      ),
    );
  }
  if (phase === "waiting" || phase === "reviewing") {
    setPrimary(
      "Go to the next step",
      true,
      "Card reader and test sale wait for this",
      () => void skipStep(),
    );
    return;
  }
  setPrimary(view.primaryLabel, view.primaryEnabled, "", () => {
    if (view.primaryAction === "next") void continueStep([]);
    else void continueOnStripe();
  });
}

function setCardError(text: string): void {
  const node = bodyEl.querySelector<HTMLElement>('[data-testid="setup-wizard-card-error"]');
  if (node !== null) node.textContent = text;
}

/** Mints the link on the tap and hands it straight to a NEW tab (STRIPETAB1), only to an https Stripe page (the simulated host
 * in test mode). The tab opens here, inside the tap and before any await, so the browser allows it; this tab stays on its
 * step. Never kept, shown or logged. */
async function continueOnStripe(setUp = false): Promise<void> {
  const active = session;
  if (active === undefined || busy) return;
  // The marker is copied into the new tab when it opens (so Stripe's return there reopens the wizard step), then dropped
  // from this tab, which is not the one Stripe returns to.
  setStripeReturnMarker(window.sessionStorage);
  const tab = openOnboardingTab();
  clearStripeReturnMarker(window.sessionStorage);
  if (tab === null) {
    setCardError(TAB_BLOCKED_LINE);
    return;
  }
  await runBusy(async () => {
    const outcome = await requestOnboardingTarget(
      tab,
      () =>
        setUp
          ? postCardPaymentsSetUp(active.apiBaseUrl, active.tenantId)
          : postCardPaymentsOnboardingLink(active.apiBaseUrl, active.tenantId),
      () => cardView?.mode,
    );
    if (!live(active.token)) return;
    if (!outcome.ok) setCardError(outcome.message);
  });
}

/** STRIPETAB1: while the card step says Stripe is reviewing, read again every 30 seconds while this tab is visible. */
const scheduleCardPoll = reviewPoller(
  () =>
    open &&
    session !== undefined &&
    screen.kind === "step" &&
    steps()[screen.index]?.id === "cardAccount" &&
    cardPhase() === "reviewing",
  () => {
    const active = session;
    if (active === undefined || busy) return;
    void (async () => {
      await refreshCardReads(active);
      if (live(active.token)) render();
    })();
  },
);

/** STRIPETAB1: this tab is visible again after Stripe's tab; read the account once, on the steps that show it. */
watchReturnFromStripe(() => {
  const active = session;
  if (!open || active === undefined || busy || screen.kind !== "step") return;
  const id = steps()[screen.index]?.id;
  if (id !== "cardAccount" && id !== "royalty") return;
  void runBusy(() => handleStripeArrival("return"));
});

/** Back from Stripe: the redirect proves nothing, so the server reads the account. A `refresh` means the link ran out. */
async function handleStripeArrival(arrival: CardPaymentsArrival): Promise<void> {
  const active = session;
  if (active === undefined) return;
  if (arrival === "refresh") {
    await refreshCardReads(active);
    if (!live(active.token)) return;
    render();
    setCardError("Link expired");
    return;
  }
  stripeArrived = true;
  await readAccountFromStripe(active);
}

async function readAccountFromStripe(active: Session): Promise<void> {
  if (hasPermission(active, "admin.payments.onboard")) {
    const result = await postCardPaymentsReturn(active.apiBaseUrl, active.tenantId);
    if (!live(active.token)) return;
    if (result.status === 200 && Array.isArray(result.body.otherLocations)) {
      cardView = result.body;
      cardLoaded = true;
      if (live(active.token)) render();
      return;
    }
  }
  await refreshCardReads(active);
  if (live(active.token)) render();
}

async function checkCardPaymentsAgain(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    const phase = cardPhase();
    if (phase === "needs" || phase === "ready") await readAccountFromStripe(active);
    else {
      await refreshCardReads(active);
      if (live(active.token)) render();
    }
  });
}

// ---- Bank accounts (BANK1; the PAY2-S6 Royalty payments step, widened) -----------------------------

/** ACCTEXPORT1 and PAYEXPORT1 (DEC-514; owner 2026-10-22: "make sure that in the new location setup wizard, that connections to gusto and
 * quickbooks are optional"). One optional step: each outside connection that is on shows its state and a Connect, and Skip for now
 * goes on. It never blocks You are ready to sell and never counts in the step total. Connect leaves for the other tool's own page and
 * comes back to Settings, Connections. */
function renderConnections(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const col = column();
  heading(col, kickerFor(step), "Connect your other tools", "Both are optional. You can connect them any time in Settings, Connections.");
  col.append(tag("Optional", "info", "setup-wizard-connections-optional"));
  const card = el("div", { cls: "ow-card", testid: "setup-wizard-connections" });
  const mayManage = hasPermission(active, "admin.location_settings.manage");
  const rows: { id: string; title: string; caption: string; connected: boolean; connect: () => Promise<string | undefined> }[] = [];
  if (accountingView !== undefined) {
    const siteId = requireDeps().deviceLocationId();
    rows.push({
      id: "quickbooks",
      title: "QuickBooks",
      caption: "Send each closed day to your books",
      connected: accountingView.connection.status === "connected",
      connect: async () => {
        if (siteId === undefined) return undefined;
        const result = await postAccountingConnectionStart(active.apiBaseUrl, active.tenantId, siteId);
        return result.status === 200 ? result.body.authorizationUrl : undefined;
      },
    });
  }
  if (payrollView !== undefined) {
    rows.push({
      id: "gusto",
      title: "Gusto",
      caption: "Send card tips to payroll",
      connected: payrollView.connection.state === "connected",
      connect: async () => {
        const result = await postPayrollConnect(active.apiBaseUrl, active.tenantId);
        return result.status === 200 ? result.body.url : undefined;
      },
    });
  }
  for (const row of rows) {
    const line = el(
      "div",
      { cls: "ow-keyrow", testid: "setup-wizard-connection-row", attrs: { "data-row": row.id, "data-connected": String(row.connected) } },
      el("span", {}, el("span", { cls: "ow-bank-title", text: row.title }), el("span", { cls: "ow-hint ow-bank-caption", text: row.caption })),
    );
    line.append(tag(row.connected ? "\u2713 Connected" : "Not connected", row.connected ? "ok" : "warn", "setup-wizard-connection-status"));
    if (!row.connected && mayManage) {
      line.append(
        button(`Connect ${row.title}`, "setup-wizard-connection-connect", "ow-btn", () => {
          void runBusy(async () => {
            const url = await row.connect();
            if (!live(active.token)) return;
            if (url === undefined || !isFollowableConsentUrl(url)) {
              const failed = bodyEl.querySelector<HTMLElement>('[data-testid="setup-wizard-connections-error"]');
              if (failed !== null) failed.textContent = "That did not work. Try again, or connect later in Settings.";
              return;
            }
            window.location.assign(url);
          });
        }),
      );
    }
    card.append(line);
  }
  card.append(el("div", { cls: "ow-error", testid: "setup-wizard-connections-error", attrs: { role: "alert" } }));
  col.append(card);
  if (anyConnectionMade()) {
    setPrimary("Continue", true, "", () => void continueStep(["connections"]));
    return;
  }
  setPrimary("Skip for now", true, "Optional. Connect later in Settings, Connections", () => void skipStep());
}

function renderBankAccounts(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const view = bankView();
  const col = column();
  heading(col, kickerFor(step), "Bank accounts");
  const status = el("div", {
    cls: "ow-card",
    testid: "setup-wizard-bank-accounts",
    attrs: { "data-done": String(view.debitsConnected) },
  });
  status.append(
    el(
      "div",
      { cls: "ow-keyrow" },
      el("span", { text: "Bank debits" }),
      tag(view.debitsConnected ? "Done" : "To do", view.debitsConnected ? "ok" : "warn", "setup-wizard-bank-tag"),
    ),
  );
  for (const row of view.rows) status.append(...bankRowNodes(row));
  status.append(
    el("div", { cls: "ow-error", testid: "setup-wizard-bank-error", attrs: { role: "alert" } }),
  );
  col.append(status);
  if (view.debitsConnected) {
    setPrimary("Continue", true, "", () => void continueStep([]));
    return;
  }
  setPrimary("Go to the next step", true, "Connect these later in Settings", () => void skipStep());
}

/** One row: title and caption, "Account ending 1234" or a waiting line, the status, and at most one action. A debit
 * authorization first shows its text and asks for Continue on Stripe, so one stray tap authorizes nothing. */
function bankRowNodes(row: BankRowView): HTMLElement[] {
  const label = el(
    "span",
    {},
    el("span", { cls: "ow-bank-title", text: row.title }),
    el("span", { cls: "ow-hint ow-bank-caption", text: row.caption }),
  );
  const line = el(
    "div",
    {
      cls: "ow-keyrow",
      testid: "setup-wizard-bank-row",
      attrs: { "data-row": row.id, "data-status": row.status },
    },
    label,
  );
  const value = el("b", {
    cls: "ow-bank-detail",
    text: row.detail ?? "",
    testid: "setup-wizard-bank-row-detail",
  });
  line.append(value, tag(`${row.chip.symbol === "" ? "" : `${row.chip.symbol} `}${row.chip.label}`, row.status === "connected" ? "ok" : "warn", "setup-wizard-bank-row-status"));
  if (row.viewOnly) line.append(tag("View only", "info", "setup-wizard-bank-row-view-only"));
  if (row.action !== null) {
    line.append(
      button(row.action.label, "setup-wizard-bank-row-action", "ow-btn", () => onBankAction(row)),
    );
  }
  const nodes: HTMLElement[] = [line];
  if (bankConfirming === row.id && row.disclosure !== null) {
    nodes.push(
      el("p", {
        cls: "ow-note",
        text: row.disclosure,
        testid: "setup-wizard-bank-row-disclosure",
      }),
      el(
        "div",
        { cls: "ow-stack" },
        button("Continue on Stripe", "setup-wizard-bank-row-continue", "ow-btn ow-btn-wide", () => {
          void continueOnDebitStripe(row.id === "royalties" ? "royalties" : "subscription");
        }),
        button("Cancel", "setup-wizard-bank-row-cancel", "ow-btn ow-btn-wide", () => {
          bankConfirming = undefined;
          render();
        }),
      ),
    );
  }
  return nodes;
}

function onBankAction(row: BankRowView): void {
  if (row.id === "deposits") {
    if (row.action?.kind === "change") {
      window.open(payoutSettingsUrl(cardView?.mode), "_blank", "noopener,noreferrer");
      return;
    }
    // The same Account Link as the Card payments step (created first when no account exists); its return opens that step.
    void continueOnStripe(row.action?.kind === "setup");
    return;
  }
  bankConfirming = bankConfirming === row.id ? undefined : row.id;
  render();
}

function setBankError(text: string): void {
  const node = bodyEl.querySelector<HTMLElement>('[data-testid="setup-wizard-bank-error"]');
  if (node !== null) node.textContent = text;
}

async function continueOnDebitStripe(which: "royalties" | "subscription"): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    const result =
      which === "royalties"
        ? await postRoyaltyAuthorizationStart(active.apiBaseUrl, active.tenantId)
        : await postSubscriptionDebitAuthorizationStart(active.apiBaseUrl, active.tenantId);
    if (!live(active.token)) return;
    if (result.status === 200 && typeof result.body.url === "string") {
      const target = onboardingNavigationTarget(
        result.body.url,
        which === "royalties" ? royaltyView?.mode : subscriptionView?.mode,
      );
      if (target === null) {
        setBankError("Link not valid");
        return;
      }
      if (which === "royalties") setRoyaltyReturnMarker(window.sessionStorage);
      else setSubscriptionDebitReturnMarker(window.sessionStorage);
      window.location.assign(target);
      return;
    }
    if (result.status === 200) {
      bankConfirming = undefined;
      await refreshCardReads(active);
      if (live(active.token)) render();
      return;
    }
    setBankError(result.body.message ?? result.body.error ?? "Could not open Stripe");
  });
}

/** Back from a hosted bank debit page: the redirect proves nothing, so the server reads the session. */
async function handleBankArrival(
  which: "royalties" | "subscription",
  arrival: "return" | "cancel",
): Promise<void> {
  const active = session;
  if (active === undefined) return;
  bankConfirming = undefined;
  if (arrival === "return" && hasPermission(active, "admin.payments.onboard")) {
    const result =
      which === "royalties"
        ? await postRoyaltyAuthorizationReturn(active.apiBaseUrl, active.tenantId)
        : await postSubscriptionDebitAuthorizationReturn(active.apiBaseUrl, active.tenantId);
    if (!live(active.token)) return;
    if (result.status === 200 && result.body.applies === true) {
      if (which === "royalties") royaltyView = result.body;
      else subscriptionView = result.body;
    }
  } else {
    await refreshCardReads(active);
    if (!live(active.token)) return;
  }
  render();
}

// ---- 11. Connect your card reader -----------------------------------------------------------------

/** The reader waits for the card account only while connected accounts are live. Dark, nothing is waited on. */
function readerGate(): string {
  return cardNeedsAccount() ? "Card payments are not on yet" : "";
}

function renderReader(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const col = column(true);
  heading(col, kickerFor(step), step.title);
  const canRegister = hasPermission(active, "admin.terminal.manage");

  if (locationReader !== undefined) {
    col.append(
      card(
        "setup-wizard-reader-paired",
        el(
          "div",
          { cls: "ow-keyrow", testid: "setup-wizard-reader-row" },
          el("span", { text: "Card reader" }),
          el("b", { text: locationReader.display_name }),
          tag(
            readerConnectionTag(locationReader.reader?.connection).label,
            readerConnectionTag(locationReader.reader?.connection).ok ? "ok" : "warn",
          ),
        ),
      ),
    );
    setPrimary("Continue", true, "", () => void continueStep([]));
    return;
  }

  const words: HTMLInputElement[] = [];
  const boxes = el("div", { cls: "ow-words", testid: "ow-reader-words" });
  const error = el("div", {
    cls: "ow-error",
    testid: "setup-wizard-reader-error",
    attrs: { role: "alert" },
  });
  for (let i = 0; i < 3; i += 1) {
    const input = el("input", {
      cls: "ow-input ow-word",
      testid: `ow-reader-word-${i + 1}`,
      attrs: {
        id: `ow-reader-word-${i + 1}`,
        type: "text",
        inputmode: "text",
        autocomplete: "off",
        autocapitalize: "none",
        autocorrect: "off",
        spellcheck: "false",
        placeholder: "word",
        "aria-label": `Word ${i + 1}`,
      },
    });
    input.value = readerWords[i] ?? "";
    input.addEventListener("input", () => {
      readerWords[i] = input.value;
      refresh();
    });
    words.push(input);
    boxes.append(input);
    if (i < 2)
      boxes.append(el("span", { cls: "ow-dash", text: "-", attrs: { "aria-hidden": "true" } }));
  }
  const stepsList = el(
    "div",
    { cls: "ow-steps" },
    readerStep(1, "Turn the reader on", "Connect it to your Wi-Fi."),
    readerStep(2, "Find the 3 words on its screen"),
    readerStep(3, "Type them here"),
  );
  const right = el(
    "div",
    { cls: "ow-reader-right" },
    stepsList,
    el("div", { cls: "ow-label", text: "The 3 words" }),
    boxes,
    error,
  );
  col.append(
    el(
      "div",
      { cls: "ow-cols" },
      el("div", { cls: "ow-illus", testid: "ow-reader-drawing" }, readerDrawing()),
      right,
    ),
  );
  const refresh = (): void => {
    const gate = readerGate();
    const code = readerCodeFromWords(readerWords);
    const reason = !canRegister
      ? "Not allowed"
      : gate !== ""
        ? gate
        : code === ""
          ? READER_NEEDS_WORDS
          : "";
    setPrimary("Connect reader", reason === "", reason, () => void pairReader(false));
    if (testEnvironment) {
      secondaryButton.hidden = false;
      secondaryButton.textContent = "Use a test reader";
      secondaryAction = () => void pairReader(true);
      secondaryButton.disabled = !canRegister || gate !== "";
    }
    setSecondaryMore("Skip for now", () => void skipStep());
  };
  refresh();
}

function readerStep(n: number, text: string, sub?: string): HTMLElement {
  return el(
    "div",
    { cls: "ow-stepline" },
    el("span", { cls: "ow-stepnum", text: String(n) }),
    el(
      "div",
      { cls: "ow-steptext" },
      el("b", { text }),
      ...(sub === undefined ? [] : [el("small", { text: sub })]),
    ),
  );
}

/** Registers through the route Settings uses. A test reader sends no code. The words go only to that request: they are cleared
 * from the form at once and never logged. */
async function pairReader(testReader: boolean): Promise<void> {
  const active = session;
  if (active === undefined) return;
  const code = testReader ? "" : readerCodeFromWords(readerWords);
  if (!testReader && code === "") return;
  await runBusy(async () => {
    const result = await registerTerminal(active.apiBaseUrl, active.tenantId, {
      idempotencyKey: crypto.randomUUID(),
      displayName: FIRST_READER_NAME,
      ...(code === "" ? {} : { registrationCode: code }),
    });
    if (!live(active.token)) return;
    if (result.status !== 201 && result.status !== 200) {
      const node = bodyEl.querySelector<HTMLElement>('[data-testid="setup-wizard-reader-error"]');
      if (node !== null)
        node.textContent =
          result.body.message ?? result.body.error ?? "Could not connect the reader";
      return;
    }
    readerWords = ["", "", ""];
    // The device takes the location's only reader, exactly as after a registration in Settings.
    window.dispatchEvent(new Event(CARD_READER_REGISTERED_EVENT));
    await refreshCardReads(active);
    if (live(active.token)) render();
  });
}

// ---- 12. Try one sale -----------------------------------------------------------------------------

function stopTestSalePolling(): void {
  if (testSaleTimer !== undefined) clearTimeout(testSaleTimer);
  testSaleTimer = undefined;
}

function renderTestSale(step: OwnerStepDef): void {
  const active = session;
  if (active === undefined) return;
  const col = column();
  // LIVETEST1 (DEC-600): on a live location the step is a real $1.00 charge, refunded right away, and says so before Start.
  const liveCard = cardView?.mode === "live";
  heading(col, "Last step", step.title, testSaleIntro(liveCard));
  const canRun = hasPermission(active, "admin.payments.onboard");
  const outcome = testSale === undefined ? undefined : testSaleOutcome(testSale.status, liveCard);
  const body = el("div", { cls: "ow-card ow-testcard", testid: "setup-wizard-test-sale" });
  body.append(
    el("div", { cls: "ow-label", text: "Test sale" }),
    el("div", {
      cls: "ow-hugevalue",
      text: formatCents(100),
      testid: "setup-wizard-test-sale-amount",
    }),
  );
  if (testSale !== undefined && outcome !== undefined) {
    body.append(
      el(
        "div",
        {
          cls: "ow-keyrow ow-center",
          testid: "setup-wizard-test-sale-status",
          attrs: { "data-status": testSale.status },
        },
        tag(
          testSale.status === "waiting" ? "Waiting for a card on the reader" : outcome.label,
          outcome.ok
            ? "ok"
            : testSale.status === "waiting" || (liveCard && testSale.status === "refund_pending")
              ? "info"
              : "warn",
        ),
        ...(testSale.status !== "waiting" && testSale.message !== ""
          ? [el("span", { cls: "ow-sub", text: testSale.message })]
          : []),
      ),
    );
  }
  body.append(
    el("div", {
      cls: "ow-error",
      testid: "setup-wizard-test-sale-error",
      attrs: { role: "alert" },
    }),
  );
  col.append(body);
  col.append(
    el("div", {
      cls: "ow-banner ow-banner-info",
      text: testSaleNotice(liveCard),
      testid: "setup-wizard-test-sale-notice",
    }),
  );

  if (outcome === undefined) {
    const reason =
      readerGate() !== ""
        ? readerGate()
        : locationReader === undefined
          ? "Connect a card reader first"
          : canRun
            ? ""
            : "Not allowed";
    setPrimary("Start test sale", reason === "", reason, () => void runTestSale());
    setSecondary("Skip for now", () => void skipStep());
    return;
  }
  if (outcome.done) {
    setPrimary("Continue", true, "", () => void continueStep(["testSale"], { refresh: false }));
    return;
  }
  if (outcome.canRetry) {
    setPrimary("Try again", true, "", () => void runTestSale());
    setSecondary("Skip for now", () => void skipStep());
    return;
  }
  if (outcome.polling) {
    // On the reader, or the refund is not confirmed: the poll keeps asking and nothing can be started over.
    setPrimary(
      testSale?.status === "waiting" ? "Waiting for the card" : liveCard ? "Refunding" : "Refund pending",
      false,
      "",
      () => {},
    );
    return;
  }
  // Refund failed: shown as not done. Continue leaves it on What is left.
  setPrimary("Continue", true, "", () => void skipStep());
}

function applyTestSale(sale: CardTestSaleDto): void {
  if (testSale === undefined || testSale.id !== sale.testSaleId.toLowerCase()) return;
  testSale = { id: testSale.id, status: sale.status, message: sale.message };
  const outcome = testSaleOutcome(sale.status, cardView?.mode === "live");
  if (outcome.done) testSaleApproved = true;
  if (!outcome.polling) stopTestSalePolling();
  if (open && screen.kind === "step" && steps()[screen.index]?.id === "testSale") render();
}

function scheduleTestSalePoll(active: Session, id: string): void {
  stopTestSalePolling();
  testSaleTimer = setTimeout(() => {
    void (async () => {
      if (!live(active.token) || testSale?.id !== id) return;
      const result = await getCardTestSale(active.apiBaseUrl, active.tenantId, id);
      if (!live(active.token) || testSale?.id !== id) return;
      // 404: the start has not written yet, so the sale is still starting. Any other miss is asked again.
      if (result.status === 200) applyTestSale(result.body.testSale);
      if (testSale?.id === id && testSaleOutcome(testSale.status, cardView?.mode === "live").polling) {
        scheduleTestSalePoll(active, id);
      }
    })();
  }, TEST_SALE_POLL_MS);
}

/** Starts a sale under a new client id, so a retry and a poll name the same sale and a fresh try never reuses one. The start
 * waits for the reader on the server; the poll runs meanwhile and ends on whichever answers first. */
async function runTestSale(): Promise<void> {
  const active = session;
  const reader = locationReader;
  if (active === undefined || reader === undefined || testSale?.status === "waiting") return;
  const id = crypto.randomUUID().toLowerCase();
  testSale = { id, status: "waiting", message: "" };
  render();
  scheduleTestSalePoll(active, id);
  const result = await startCardTestSale(active.apiBaseUrl, active.tenantId, {
    testSaleId: id,
    terminalId: reader.id,
  });
  if (!live(active.token) || testSale?.id !== id) return;
  if (result.status === 200 && result.body.testSale !== undefined) {
    applyTestSale(result.body.testSale);
    return;
  }
  // Refused before any card was asked for (reader offline, card payments off): nothing started.
  stopTestSalePolling();
  testSale = undefined;
  render();
  const node = bodyEl.querySelector<HTMLElement>('[data-testid="setup-wizard-test-sale-error"]');
  if (node !== null) {
    node.textContent = result.body.message ?? result.body.error ?? "Could not start the test sale";
  }
}

// ---------------------------------------------------------------------------------------------------
// Welcome back, ready, what is left
// ---------------------------------------------------------------------------------------------------

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? full;
}

/** LEGAL1-F1: the one required agreement. Continue stays off until the box is ticked; the server records who, the version and the
 * time. The links open the published pages in a new tab. */
function renderTerms(): void {
  const active = session;
  if (active === undefined) return;
  const col = column(true);
  heading(col, "Before you start", "Agree to the terms", "Read them first, then tick the box to continue.");
  const checkbox = el("input", {
    attrs: { type: "checkbox", id: "setup-wizard-terms" },
    testid: "setup-wizard-terms",
  });
  checkbox.checked = termsTicked;
  const link = (text: string, href: string, testid: string): HTMLElement =>
    el("a", { text, testid, attrs: { href, target: "_blank", rel: "noopener noreferrer" } });
  const label = el(
    "label",
    { cls: "legal-agree", attrs: { for: "setup-wizard-terms" } },
    checkbox,
    el(
      "span",
      {},
      "I agree to the ",
      link("Terms of Service", LEGAL_TERMS_URL, "setup-wizard-terms-link"),
      " and ",
      link("Privacy Policy", LEGAL_PRIVACY_URL, "setup-wizard-privacy-link"),
    ),
  );
  col.append(card("ow-terms", label));
  const reason = "Tick the box to continue.";
  setPrimary("Continue", termsTicked, termsTicked ? "" : reason, () => void acceptTerms());
  checkbox.addEventListener("change", () => {
    termsTicked = checkbox.checked;
    setPrimary("Continue", termsTicked, termsTicked ? "" : reason, () => void acceptTerms());
  });
}

async function acceptTerms(): Promise<void> {
  const active = session;
  if (active === undefined || screen.kind !== "terms" || !termsTicked) return;
  await runBusy(async () => {
    const result = await postLegalAcceptance(active.apiBaseUrl, active.tenantId, LEGAL_TERMS_VERSION);
    if (!live(active.token)) return;
    if (result.status !== 200) {
      showReason("The agreement was not saved. Try again.");
      return;
    }
    screen = afterTerms;
    render();
  });
}

function renderWelcomeBack(): void {
  const active = session;
  if (active === undefined) return;
  const view = welcomeBackView(steps(), stepStates());
  const col = column(true);
  col.append(
    el("div", {
      cls: "ow-kicker",
      text: `Welcome back, ${firstName(active.employeeId)}`,
      testid: "setup-wizard-eyebrow",
    }),
    el("h1", {
      cls: "ow-title",
      text: view.title,
      testid: "setup-wizard-title",
      attrs: { tabindex: "-1" },
    }),
    el("p", { cls: "ow-hint", text: "Everything you did is saved." }),
  );
  if (view.next !== undefined) {
    col.append(
      card(
        "ow-back-next",
        el("div", { cls: "ow-label", text: "Next up" }),
        el("div", {
          cls: "ow-big",
          text: `Step ${view.next.number}: ${view.next.title}`,
          testid: "ow-back-next-title",
        }),
        el("div", {
          cls: "ow-sub",
          text: `About ${view.next.minutes} ${view.next.minutes === 1 ? "minute" : "minutes"}`,
        }),
      ),
    );
  }
  const lists = el("div", { cls: "ow-cols" });
  const doneCard = card("ow-back-done", el("div", { cls: "ow-label", text: "Done" }));
  for (const line of view.done)
    doneCard.append(
      el("div", { cls: "ow-checkline", text: `✓ ${line}`, testid: "ow-back-done-line" }),
    );
  const todoCard = card("ow-back-todo", el("div", { cls: "ow-label", text: "Still to do" }));
  for (const line of view.todo)
    todoCard.append(el("div", { cls: "ow-checkline", text: line, testid: "ow-back-todo-line" }));
  lists.append(doneCard, todoCard);
  col.append(lists);
  setSecondary("Go to my tabs", () => void leaveToTabs());
  setPrimary("Pick up here", true, "", () => void pickUp());
}

async function pickUp(): Promise<void> {
  const active = session;
  if (active === undefined) return;
  await runBusy(async () => {
    if (!(await commit({ paused: false }))) return;
    if (!live(active.token)) return;
    const at = firstUnfinishedIndex(stepStates());
    if (at === undefined) {
      await showReady();
      return;
    }
    screen = { kind: "step", index: at };
    render();
  });
}

function renderReady(): void {
  const active = session;
  if (active === undefined) return;
  const list = steps();
  const states = stepStates();
  const tested = testSaleApproved || progress.reviewedSteps.includes("testSale");
  const view = readyView(list, states, tested);
  const col = column(true);
  heading(col, view.ready ? "Setup done" : "Almost there", view.title, view.hint);
  const lines = el("div", { cls: "ow-list", testid: "ow-ready-lines" });
  for (const line of readyLines(list, states)) {
    lines.append(
      el(
        "div",
        {
          cls: "ow-li",
          testid: "ow-ready-line",
          attrs: { "data-state": line.done ? "done" : "todo" },
        },
        el("span", {
          cls: line.done ? "ow-check" : "ow-circle",
          text: line.done ? "✓" : "",
          attrs: { "aria-hidden": "true" },
        }),
        el("div", { cls: "ow-li-main" }, el("div", { cls: "ow-li-title", text: line.label })),
      ),
    );
  }
  const left = whatsLeftView(list, states, reads());
  const count = left.canWait.length;
  const aside = card(
    "ow-ready-left",
    el("div", { cls: "ow-label", text: "Can wait" }),
    el("div", { cls: "ow-hugevalue", text: String(count), testid: "ow-ready-left-count" }),
    el("div", {
      cls: "ow-sub",
      text: count === 1 ? "thing left for later" : "things left for later",
    }),
    button("See what is left", "ow-ready-see-left", "ow-btn ow-btn-wide", showLeft),
  );
  col.append(el("div", { cls: "ow-cols" }, lines, aside));
  if (!view.ready) {
    for (const blocker of view.blockers) {
      col.append(
        el("div", { cls: "ow-banner ow-banner-warn", text: blocker, testid: "ow-ready-blocker" }),
      );
    }
  }
  setPrimary("Open tabs", true, "", () => void leaveToTabs());
  primaryButton.classList.add("ow-btn-good");
  if (list.length > 0) {
    const lastIndex = list.length - 1;
    setBack(() => {
      screen = { kind: "step", index: lastIndex };
      render();
    });
  }
}

function renderWhatsLeft(): void {
  const active = session;
  if (active === undefined) return;
  const list = steps();
  const states = stepStates();
  const view = whatsLeftView(list, states, reads());
  const col = column(true);
  col.append(
    el("div", {
      cls: "ow-kicker",
      text: requireDeps().locationName() ?? "Your location",
      testid: "setup-wizard-eyebrow",
    }),
    el("h1", {
      cls: "ow-title",
      text: "What is left",
      testid: "setup-wizard-title",
      attrs: { tabindex: "-1" },
    }),
    el("p", { cls: "ow-hint", text: "None of these stop you from selling. Do them in any order." }),
  );
  const left = el(
    "div",
    { cls: "ow-leftcol" },
    el("div", { cls: "ow-grouphead", text: "Can wait" }),
  );
  const waiting = el("div", { cls: "ow-list", testid: "ow-left-wait" });
  for (const extra of view.canWait) {
    const row = el(
      "div",
      {
        cls: "ow-li",
        testid: "ow-left-row",
        attrs: { "data-extra": extra.id, "data-state": extra.state },
      },
      el("span", { cls: "ow-circle", attrs: { "aria-hidden": "true" } }),
      el(
        "div",
        { cls: "ow-li-main" },
        el("div", { cls: "ow-li-title", text: extra.label }),
        ...(extra.state === "asked"
          ? [el("div", { cls: "ow-li-sub", text: "Asked, Snip & Sip will help" })]
          : []),
      ),
    );
    row.append(
      button(extra.state === "asked" ? "Ask again" : extra.action, "ow-left-go", "ow-btn", () => {
        screen = { kind: "extra", id: extra.id };
        render();
      }),
    );
    waiting.append(row);
  }
  left.append(waiting);
  const right = el("div", { cls: "ow-leftcol" });
  if (view.skipped.length > 0) {
    right.append(el("div", { cls: "ow-grouphead", text: "Skipped" }));
    const skippedList = el("div", { cls: "ow-list", testid: "ow-left-skipped" });
    for (const entry of view.skipped) {
      const row = el(
        "div",
        { cls: "ow-li", testid: "ow-left-skipped-row", attrs: { "data-step": entry.id } },
        el("span", {
          cls: "ow-circle ow-circle-wait",
          text: "!",
          attrs: { "aria-hidden": "true" },
        }),
        el(
          "div",
          { cls: "ow-li-main" },
          el("div", { cls: "ow-li-title", text: `Step ${entry.number}: ${entry.label}` }),
          el("div", {
            cls: "ow-li-sub",
            text: progress.skippedSteps.includes(entry.id) ? "Skipped" : "Not done",
          }),
        ),
      );
      row.append(button("Do it", "ow-left-do", "ow-btn", () => goToStep(entry.id)));
      skippedList.append(row);
    }
    right.append(skippedList);
  }
  right.append(el("div", { cls: "ow-grouphead", text: "Done" }));
  right.append(
    card(
      "ow-left-done",
      el(
        "div",
        { cls: "ow-keyrow" },
        el("span", { cls: "ow-check", text: "✓", attrs: { "aria-hidden": "true" } }),
        el("b", {
          text: `${view.doneCount} of ${view.totalSteps} steps`,
          testid: "ow-left-done-count",
        }),
      ),
    ),
  );
  col.append(el("div", { cls: "ow-cols" }, left, right));
  setBack(() => void showReady());
  setPrimary("Open tabs", true, "", () => void leaveToTabs());
  primaryButton.classList.add("ow-btn-good");
}

// ---------------------------------------------------------------------------------------------------
// Extras
// ---------------------------------------------------------------------------------------------------

function renderExtra(id: OwnerExtraId): void {
  const active = session;
  if (active === undefined) return;
  const host: ExtraHost = {
    session: active,
    locationId: requireDeps().deviceLocationId(),
    body: bodyEl,
    live: () => live(active.token),
    column,
    heading: (col, kicker, title, hint) => heading(col, kicker, title, hint),
    setPrimary,
    setSecondary,
    setBack,
    showReason,
    runBusy,
    rerender: render,
    reviewed: (extra) => progress.reviewedSteps.includes(extra),
    /** Records that an extra was reviewed, refreshes the reads and goes back to What is left. */
    finish: async (extra, options) => {
      if (!(await commit({ review: options?.review === false ? [] : [extra] }))) return;
      await refreshReads(active);
      if (!live(active.token)) return;
      showLeft();
    },
    back: () => showLeft(),
  };
  renderExtraScreen(id, host);
}

// ---------------------------------------------------------------------------------------------------
// Footer wiring, once
// ---------------------------------------------------------------------------------------------------

primaryButton.addEventListener("click", () => {
  primaryAction?.();
});
secondaryButton.addEventListener("click", () => {
  secondaryAction?.();
});
secondaryMoreButton.addEventListener("click", () => {
  secondaryMoreAction?.();
});
backButton.addEventListener("click", () => {
  backAction?.();
});
finishLaterButton.addEventListener("click", () => {
  void saveAndFinishLater();
});
